// src/Contacts/Contacts.js
// Contacts — a contractor's CRM (STATIC PREVIEW; all data is local mock data).
//   • Relationships — companies/"big dogs" cultivated toward a finish line.
//   • People — individuals you talk to. A person has an optional company and
//     attaches to projects; conversations are logged on the PERSON and tagged
//     with a PROJECT, so third parties (inspectors, designers, a sub's engineer)
//     and one inspector across many jobs are all handled cleanly.
//   • Bids & Jobs — pipeline opportunities, each tagged with a SOURCE.
// Self-contained. Logging / adding mutates in-memory state so it feels real.
import React, { useMemo, useState, useRef, useEffect } from 'react';
import axios from 'axios';
import exifr from 'exifr';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../apiBase';
import { getAddressSuggestions, geocodePlaceId } from '../lib/googlePlaces';
import { parseAddrString } from '../lib/orgAddress';
import ImageLightbox from '../lib/ImageLightbox';
import NiceSelect from '../lib/NiceSelect';
import LogoCropModal from '../lib/LogoCropModal';
import { EventBadge, EventTypePicker } from '../lib/eventTypes';
import JobsiteMiniMap from '../lib/JobsiteMiniMap';
import { fmtPhone, fmtPhoneInput } from '../lib/phone';
import {
  Phone, Voicemail, MessageSquare, Mail, StickyNote, Plus, Search, Star,
  Globe, MapPin, X, CalendarClock, PhoneCall, ArrowUpRight, ArrowDownLeft,
  Building2, Users as UsersIcon, CheckCircle2, TrendingUp, ChevronDown, Target,
  Briefcase, DollarSign, Trophy, Flame, LayoutGrid, List as ListIcon,
  FileText, Link2, Clock, Image as ImageIcon, Camera, ListChecks, User, FolderOpen,
  Copy, Check, Smartphone, Pencil, ZoomIn, ZoomOut, Trash2, ArrowLeft,
} from 'lucide-react';
import {
  SEED_CONTACTS, SEED_PEOPLE, SEED_CUSTOMERS, REL_STAGES, REL_STAGE_META,
  TYPE_META, REL_TYPES, CHANNELS, SOURCES, SOURCE_TONE, BID_STAGES, BID_STAGE_META, CSLB_CLASSES,
  PROJECTS, projectName,
} from './contactsData';

const CHANNEL_ICON = { call: Phone, voicemail: Voicemail, text: MessageSquare, email: Mail, note: StickyNote };

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
const money = (n) => '$' + Number(n || 0).toLocaleString();
const moneyK = (n) => (n >= 1000 ? `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : `$${n}`);
const timeAgo = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now'; if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const followLabel = (iso) => {
  if (!iso) return null;
  const day = new Date(iso); day.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((day - today) / 86400000);
  if (days < 0) return { label: `Overdue ${-days}d`, tone: 'bg-red-500/15 text-red-300 ring-red-500/30' };
  if (days === 0) return { label: 'Due today', tone: 'bg-amber-500/15 text-amber-300 ring-amber-500/30' };
  if (days === 1) return { label: 'Due tomorrow', tone: 'bg-amber-500/10 text-amber-300/90 ring-amber-500/20' };
  return { label: `In ${days}d`, tone: 'bg-zinc-700/40 text-zinc-300 ring-zinc-600/30' };
};
const gradients = ['from-indigo-500 to-violet-600', 'from-cyan-500 to-blue-600', 'from-emerald-500 to-teal-600', 'from-amber-500 to-orange-600', 'from-fuchsia-500 to-pink-600', 'from-sky-500 to-indigo-600'];
const avatarGrad = (id = '') => gradients[(String(id).charCodeAt(String(id).length - 1) || 0) % gradients.length];

function ChannelBadge({ channel }) {
  const Icon = CHANNEL_ICON[channel] || StickyNote;
  const c = CHANNELS[channel] || CHANNELS.note;
  return <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${c.bg} ${c.tone} ring-1 ring-white/5`}><Icon className="w-4 h-4" /></span>;
}
function SourceTag({ source }) {
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${SOURCE_TONE[source] || 'bg-zinc-700/40 text-zinc-300'}`}>{source}</span>;
}

// Company logo squircle — shows initials until a logo is uploaded, then the image
// (focal point fx/fy + zoom z). Editable version adds an upload/adjust button with
// drag-to-reposition + a zoom slider (mirrors the website "selected work" tiles).
function CompanyLogo({ contact, onUpdate, editable = false, sizeClass = 'w-10 h-10', roundedClass = 'rounded-xl' }) {
  // Logo is a tiny pre-cropped ≤150px data-URL (made in LogoCropModal). Older
  // demo rows stored { src, fx, fy, z } — display those too.
  const [cropOpen, setCropOpen] = useState(false);
  const src = typeof contact.logo === 'string' && contact.logo ? contact.logo : contact.logo?.src || null;
  return (
    <div className="relative shrink-0">
      <div className={`${sizeClass} ${roundedClass} overflow-hidden bg-gradient-to-br ${avatarGrad(contact.id)} flex items-center justify-center text-white font-bold`}>
        {src
          ? <img src={src} alt="" draggable={false} className="w-full h-full object-cover select-none" />
          : <span>{initials(contact.company)}</span>}
      </div>
      {editable && (
        <button
          onClick={() => setCropOpen(true)}
          title={src ? 'Change logo' : 'Add logo'}
          className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white flex items-center justify-center shadow ring-2 ring-gray-950"
        >
          {src ? <Pencil className="w-3 h-3" /> : <Camera className="w-3 h-3" />}
        </button>
      )}
      {cropOpen && (
        <LogoCropModal
          onClose={() => setCropOpen(false)}
          onSave={(dataUrl) => { onUpdate && onUpdate(contact.id, { logo: dataUrl }); setCropOpen(false); }}
        />
      )}
    </div>
  );
}

// ---- map real DB docs into the shapes this page renders ----
const isMongoId = (id) => /^[0-9a-f]{24}$/i.test(String(id));
const mapCustomer = (c) => ({
  kind: 'customer', id: c._id, name: c.name, nickname: c.nickname || '', type: c.type, status: c.status,
  phone: c.primaryPhone || '', email: c.primaryEmail || '',
  secondaryPhone: c.secondaryPhone || '', altEmail: c.altEmail || '',
  company: c.businessName || '', city: (c.billingAddress && c.billingAddress.city) || '',
  // full billing address so drawers can show more than the city
  address: c.billingAddress ? {
    street: c.billingAddress.street || '', city: c.billingAddress.city || '',
    state: c.billingAddress.state || '', zip: c.billingAddress.zip || '',
  } : null,
  notes: c.notes || '', activities: [], _real: true,
});
const mapBid = (b) => ({
  id: b._id, title: b.title,
  // no linked customer yet → the typed contact is who you talk to
  who: (b.customer && b.customer.name) || b.contactName || '—',
  type: (b.customer && b.customer.type) || '', customerId: b.customer && b.customer._id,
  customerPhone: (b.customer && b.customer.primaryPhone) || b.contactPhone || '',
  customerEmail: (b.customer && b.customer.primaryEmail) || b.contactEmail || '',
  contactName: b.contactName || '', contactPhone: b.contactPhone || '', contactEmail: b.contactEmail || '',
  value: b.value || 0, stage: b.stage, source: b.source || '', due: b.due,
  quotedPrice: b.quotedPrice ?? null, quotedNote: b.quotedNote || '', quotedAt: b.quotedAt || null,
  notes: b.notes || '', jobSiteAddress: b.jobSiteAddress,
  activities: b.activities || [], project: b.project, contactId: null, _real: true,
});
const authH = () => ({ headers: { Authorization: localStorage.getItem('token') } });

// Caption for bid image/video tiles — the SAME rule as the project gallery:
// the date the shot was TAKEN ("Sun, August 30, 2026"), read from the
// filename's YYYY-MM-DD prefix; falls back to the file's own device date;
// shows the filename only when no date is known. Hover still reveals the name.
const TILE_DATE_OPTS = { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' };
const tileCaption = (f) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(f.name || '');
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-US', TILE_DATE_OPTS);
  const d = f.client_modified || f.server_modified;
  if (d) return new Date(d).toLocaleDateString('en-US', TILE_DATE_OPTS);
  return f.name;
};

// EXIF taken-date → filename, matching the project pipeline's convention
// (YYYY-MM-DD_HH.MM.SS_name). The date lives IN the filename, so it survives
// promotion and every folder move — project galleries caption it anywhere the
// file lands. EXIF (authoritative) for photos; the file's own device date as
// the fallback for videos/documents. Already-stamped names pass through.
const stampFileName = async (file) => {
  if (/^\d{4}-\d{2}-\d{2}[_.]/.test(file.name)) return file; // already carries a date
  let taken = null;
  if (file.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|hei[cf])$/i.test(file.name)) {
    try {
      const exif = await exifr.parse(file, { pick: ['DateTimeOriginal', 'CreateDate'] });
      taken = (exif && (exif.DateTimeOriginal || exif.CreateDate)) || null;
    } catch (_) {}
  }
  if (!taken && file.lastModified) taken = new Date(file.lastModified);
  if (!taken) return file;
  const d = new Date(taken);
  if (isNaN(d.getTime())) return file;
  const p = (n) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}.${p(d.getMinutes())}.${p(d.getSeconds())}`;
  try {
    return new File([file], `${stamp}_${file.name}`, { type: file.type, lastModified: file.lastModified });
  } catch (_) { return file; } // ancient browser without File constructor — upload as-is
};
const mapRel = (r) => ({
  id: r._id, company: r.company, fullName: r.fullName || '', contactName: r.contactName || '', title: r.title || '',
  type: r.type || 'Contractor', licenseClass: r.licenseClass || '', licenseNumber: r.licenseNumber || '',
  relStage: r.relStage || 'Prospect', source: r.source || '', phone: r.phone || '', email: r.email || '',
  secondaryPhone: r.secondaryPhone || '', secondaryEmail: r.secondaryEmail || '',
  address: r.address || '', website: r.website || '', units: r.units ?? null,
  starred: !!r.starred, tags: r.tags || [], nextFollowUp: r.nextFollowUp || null, logo: r.logo || '',
  people: [], activities: (r.activities || []).map(a => ({ id: a._id, channel: a.channel, direction: a.direction, at: a.at, note: a.note, outcome: a.outcome, notifiedDate: a.notifiedDate || null, eventDate: a.eventDate || null, eventType: a.eventType || '' })),
  _real: true,
});

export default function Contacts() {
  // 'people' | 'relationships' | 'bids' — remembered across refreshes.
  const [tab, setTab] = useState(() => {
    const saved = localStorage.getItem('contacts.tab');
    return ['people', 'relationships', 'bids'].includes(saved) ? saved : 'people';
  });
  useEffect(() => { localStorage.setItem('contacts.tab', tab); }, [tab]);
  // One seed row stays in each tab as a worked example; everything else is live data.
  const [contacts, setContacts] = useState(SEED_CONTACTS.slice(0, 1));
  const [people, setPeople] = useState(SEED_PEOPLE.slice(0, 1).map(p => ({ ...p, name: `${p.name} (example)` })));
  // Bids tab carries real pipeline data now — no demo seed row.
  const [bids, setBids] = useState([]);
  const [customers, setCustomers] = useState([]); // real customers from the portal DB

  // live data: real customers into People, real bids into Bids & Jobs
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/customers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCustomers((r.data || []).map(mapCustomer)))
      .catch(e => console.error('Contacts: customers fetch failed', e.message));
    axios.get(`${API_BASE}/bids`, authH())
      .then(r => setBids(prev => [...(r.data || []).map(mapBid), ...prev.filter(b => b.demo)]))
      .catch(e => console.error('Contacts: bids fetch failed', e.message));
    axios.get(`${API_BASE}/relationships`, authH())
      .then(r => setContacts(prev => [...(r.data || []).map(mapRel), ...prev.filter(c => !isMongoId(c.id))]))
      .catch(e => console.error('Contacts: relationships fetch failed', e.message));
  }, []);

  // ---- bid API bridge (demo rows stay local; real rows persist) ----
  const patchBid = (id, patch) => {
    setBids(prev => prev.map(b => b.id === id ? { ...b, ...patch } : b));
    if (isMongoId(id)) {
      axios.patch(`${API_BASE}/bids/${id}`, patch, authH())
        .then(r => setBids(prev => prev.map(b => b.id === id ? mapBid(r.data) : b)))
        .catch(e => console.error('Bid update failed', e.message));
    }
  };
  const addBid = async (payload) => {
    const res = await axios.post(`${API_BASE}/bids`, payload, authH());
    const mapped = mapBid(res.data);
    setBids(prev => [mapped, ...prev]);
    return mapped; // Add Bid modal needs the id to upload staged photos
  };
  const logBidActivity = (id, entry) => {
    if (!isMongoId(id)) return;
    axios.post(`${API_BASE}/bids/${id}/activity`, entry, authH())
      .then(r => setBids(prev => prev.map(b => b.id === id ? mapBid(r.data) : b)))
      .catch(e => console.error('Bid activity failed', e.message));
  };
  const editBidActivity = (id, actId, patch) => {
    if (!isMongoId(id)) return;
    axios.patch(`${API_BASE}/bids/${id}/activity/${actId}`, patch, authH())
      .then(r => setBids(prev => prev.map(b => b.id === id ? mapBid(r.data) : b)))
      .catch(e => console.error('Bid activity edit failed', e.message));
  };
  const deleteBidActivity = (id, actId) => {
    if (!isMongoId(id)) return;
    axios.delete(`${API_BASE}/bids/${id}/activity/${actId}`, authH())
      .then(r => setBids(prev => prev.map(b => b.id === id ? mapBid(r.data) : b)))
      .catch(e => console.error('Bid activity delete failed', e.message));
  };
  const promoteBid = async (id) => {
    const res = await axios.post(`${API_BASE}/bids/${id}/promote`, {}, authH());
    setBids(prev => prev.map(b => b.id === id ? { ...b, stage: 'Won', project: res.data.project._id } : b));
    return res.data; // { project, moved }
  };
  const deleteBid = async (id) => {
    if (isMongoId(id)) await axios.delete(`${API_BASE}/bids/${id}`, authH());
    setBids(prev => prev.filter(b => b.id !== id));
  };
  // Background photo uploads after Add Bid closes — the bid's card shows a
  // progress border until its files are all in Dropbox.
  const [bidUploads, setBidUploads] = useState({}); // { bidId: { done, total, pct } }
  const uploadBidPhotos = async (bidId, files) => {
    if (!files || !files.length) return;
    // PDFs and docs dragged into the zone belong in /documents, videos in
    // /videos — not everything is a photo.
    const bucketOf = (f) => (f.type.startsWith('image/') || /\.hei[cf]$/i.test(f.name)) ? 'images'
      : f.type.startsWith('video/') ? 'videos' : 'documents';
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      setBidUploads(prev => ({ ...prev, [bidId]: { done: i, total: files.length, pct: 0 } }));
      const stamped = await stampFileName(files[i]); // taken-date into the name, like project uploads
      const fd = new FormData(); fd.append('file', stamped); fd.append('targetFolder', bucketOf(files[i]));
      try {
        await axios.post(`${API_BASE}/bids/${bidId}/upload`, fd, {
          ...authH(),
          onUploadProgress: (ev) => {
            const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
            setBidUploads(prev => ({ ...prev, [bidId]: { done: i, total: files.length, pct } }));
          },
        });
      } catch (_) { failed += 1; }
    }
    setBidUploads(prev => { const n = { ...prev }; delete n[bidId]; return n; });
    if (failed) window.alert(`${failed} photo(s) failed to upload for that bid — retry from its Files tab.`);
  };
  // Bid drawer state lives here so any tab (e.g. a person's "Jobs & paperwork")
  // can open a bid — with a back-path to the person it came from.
  const [openBidId, setOpenBidId] = useState(null);
  const [openBidSection, setOpenBidSection] = useState('overview'); // which drawer tab to land on
  const [bidBackPersonId, setBidBackPersonId] = useState(null);

  // Deep link: /contacts?bid={id}&section=quotes lands on the Bids tab with
  // that drawer open on the given section (how Create Quote's Cancel / back
  // link returns you exactly where you left).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const bidParam = searchParams.get('bid');
    if (bidParam) {
      setTab('bids');
      setOpenBidSection(searchParams.get('section') || 'overview');
      setOpenBidId(bidParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const openBidFromPerson = (bidId, personId) => {
    setSelectedPersonId(null);
    setBidBackPersonId(personId);
    setTab('bids');
    setOpenBidId(bidId);
  };
  const backToPerson = () => {
    setOpenBidId(null);
    setTab('people');
    setSelectedPersonId(bidBackPersonId);
    setBidBackPersonId(null);
  };

  // delete a person row — real customers hit the API, example rows vanish locally
  const deletePerson = async (r) => {
    if (r.kind === 'customer') {
      if (isMongoId(r.id)) await axios.delete(`${API_BASE}/customers/${r.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setCustomers(prev => prev.filter(c => c.id !== r.id));
    } else {
      setPeople(prev => prev.filter(p => p.id !== r.id));
    }
  };
  const [sources, setSources] = useState(SOURCES); // editable source list
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [selectedId, setSelectedId] = useState(null);       // company drawer
  const [selectedPersonId, setSelectedPersonId] = useState(null); // person drawer
  const [showAdd, setShowAdd] = useState(false);       // add company (Relationships)
  const [showAddPerson, setShowAddPerson] = useState(false); // add person/customer (People)
  const [personPresetCompany, setPersonPresetCompany] = useState(''); // seeded when adding from a company drawer
  const [showAddBid, setShowAddBid] = useState(false);

  const relTypes = ['All', ...REL_TYPES];
  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter(c => {
      if (typeFilter !== 'All' && c.type !== typeFilter) return false;
      if (!q) return true;
      return c.company.toLowerCase().includes(q) || c.contactName.toLowerCase().includes(q) ||
        (c.address || '').toLowerCase().includes(q) || (c.tags || []).some(t => t.toLowerCase().includes(q));
    });
  }, [contacts, search, typeFilter]);

  const selected = contacts.find(c => c.id === selectedId) || null;
  // Person drawer can be opened from a CRM person (contact) OR a plain customer
  // (e.g. a homeowner). Customers lack CRM fields, so normalize them into the
  // shape PersonDrawer expects (role/projectIds/activities) before passing in.
  const selectedPerson = useMemo(() => {
    const p = people.find(p => p.id === selectedPersonId);
    if (p) return p;
    const c = customers.find(c => c.id === selectedPersonId);
    if (c) return { ...c, role: c.type, companyId: null, companyName: '', projectIds: [], activities: c.activities || [] };
    return null;
  }, [people, customers, selectedPersonId]);

  const logActivity = (id, act) => relActivity.log(id, act);
  // Route the logged activity to whichever directory owns this id (a contact
  // person or a customer), initializing the activities array if it's missing.
  const logPersonActivity = (id, act) => {
    const entry = { ...act, id: `a${Date.now()}` };
    if (people.some(p => p.id === id)) {
      setPeople(prev => prev.map(p => p.id === id ? { ...p, activities: [entry, ...(p.activities || [])] } : p));
    } else {
      setCustomers(prev => prev.map(c => c.id === id ? { ...c, activities: [entry, ...(c.activities || [])] } : c));
    }
  };
  // Edit / delete a single logged activity (company timeline).
  const editActivity = (id, actId, patch) => relActivity.edit(id, actId, patch);
  const deleteActivity = (id, actId) => relActivity.remove(id, actId);
  // Same, for person/customer conversations — routed to whichever directory owns the id.
  const editPersonActivity = (id, actId, patch) => {
    const map = arr => (arr || []).map(a => a.id === actId ? { ...a, ...patch } : a);
    if (people.some(p => p.id === id)) setPeople(prev => prev.map(p => p.id === id ? { ...p, activities: map(p.activities) } : p));
    else setCustomers(prev => prev.map(c => c.id === id ? { ...c, activities: map(c.activities) } : c));
  };
  const deletePersonActivity = (id, actId) => {
    const drop = arr => (arr || []).filter(a => a.id !== actId);
    if (people.some(p => p.id === id)) setPeople(prev => prev.map(p => p.id === id ? { ...p, activities: drop(p.activities) } : p));
    else setCustomers(prev => prev.map(c => c.id === id ? { ...c, activities: drop(c.activities) } : c));
  };
  // Edit a person/customer — routes the patch to whichever directory owns the id.
  const updatePerson = (id, patch) => {
    if (people.some(p => p.id === id)) {
      setPeople(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
      return;
    }
    setCustomers(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
    // Real customer → persist to the portal DB (Contacts is on track to replace Customers)
    if (isMongoId(id)) {
      const payload = {};
      if (patch.name !== undefined) payload.name = patch.name;
      if (patch.phone !== undefined) payload.primaryPhone = patch.phone;
      if (patch.secondaryPhone !== undefined) payload.secondaryPhone = patch.secondaryPhone;
      if (patch.email !== undefined) payload.primaryEmail = patch.email;
      if (patch.altEmail !== undefined) payload.altEmail = patch.altEmail;
      if (patch.address !== undefined) payload.billingAddress = patch.address;
      if (patch.notes !== undefined) payload.notes = patch.notes;
      if (patch.tags !== undefined) payload.tags = patch.tags;
      if (Object.keys(payload).length) {
        axios.put(`${API_BASE}/customers/${id}`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
          .catch(e => console.error('Customer update failed', e.message));
      }
    }
  };
  // ---- relationship API bridge: optimistic local update + background save ----
  const patchRelServer = (id, patch) => {
    if (!isMongoId(id)) return; // demo rows stay local
    axios.patch(`${API_BASE}/relationships/${id}`, patch, authH())
      .catch(e => console.error('Relationship update failed', e.message));
  };
  const setRelStage = (id, relStage) => { setContacts(prev => prev.map(c => c.id === id ? { ...c, relStage } : c)); patchRelServer(id, { relStage }); };
  const toggleStar = (id) => {
    const cur = contacts.find(c => c.id === id);
    setContacts(prev => prev.map(c => c.id === id ? { ...c, starred: !c.starred } : c));
    patchRelServer(id, { starred: !cur?.starred });
  };
  const updateContact = (id, patch) => { setContacts(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c)); patchRelServer(id, patch); };
  const addRelationship = async (payload) => {
    const { _linkedIds = [], _newPeople = [], ...relPayload } = payload;
    const res = await axios.post(`${API_BASE}/relationships`, relPayload, authH());
    setContacts(prev => [mapRel(res.data), ...prev]);
    // Attach people: linking = setting their business to this company (the
    // same match the drawer and Bill-To picker use); new people are created
    // already carrying it.
    const company = String(relPayload.company || '').trim();
    // People linked to an org inherit its address as their billing address —
    // ONLY when theirs is blank (a person's own address always wins).
    const orgAddr = parseAddrString(relPayload.address);
    try {
      for (const id of _linkedIds) {
        const existing = customers.find((c) => c.id === id);
        const hasOwn = existing && existing.address && String(existing.address.street || '').trim();
        const patch = { businessName: company };
        if (!hasOwn && orgAddr) patch.billingAddress = orgAddr;
        await axios.put(`${API_BASE}/customers/${id}`, patch, authH());
      }
      for (const p of _newPeople) {
        await axios.post(`${API_BASE}/customers`, {
          name: p.name.trim(), businessName: company,
          primaryPhone: (p.phone || '').trim(), primaryEmail: (p.email || '').trim(),
          type: 'Contractor', status: 'Lead',
          ...(orgAddr ? { billingAddress: orgAddr } : {}),
        }, authH());
      }
    } catch (e) { console.error('Linking people to relationship failed:', e.message); }
    if (String(relPayload.contactName || '').trim() || _linkedIds.length || _newPeople.length) {
      axios.get(`${API_BASE}/customers`, authH())
        .then(r => setCustomers((r.data || []).map(mapCustomer)))
        .catch(() => {});
    }
  };
  const relActivity = {
    log: (id, act) => {
      if (!isMongoId(id)) { setContacts(prev => prev.map(c => c.id === id ? { ...c, activities: [{ ...act, id: `a${Date.now()}` }, ...c.activities] } : c)); return; }
      axios.post(`${API_BASE}/relationships/${id}/activity`, act, authH())
        .then(r => setContacts(prev => prev.map(c => c.id === id ? mapRel(r.data) : c)))
        .catch(e => console.error('Relationship activity failed', e.message));
    },
    edit: (id, actId, patch) => {
      if (!isMongoId(id)) { setContacts(prev => prev.map(c => c.id === id ? { ...c, activities: (c.activities || []).map(a => a.id === actId ? { ...a, ...patch } : a) } : c)); return; }
      axios.patch(`${API_BASE}/relationships/${id}/activity/${actId}`, patch, authH())
        .then(r => setContacts(prev => prev.map(c => c.id === id ? mapRel(r.data) : c)))
        .catch(e => console.error('Relationship activity edit failed', e.message));
    },
    remove: (id, actId) => {
      if (!isMongoId(id)) { setContacts(prev => prev.map(c => c.id === id ? { ...c, activities: (c.activities || []).filter(a => a.id !== actId) } : c)); return; }
      axios.delete(`${API_BASE}/relationships/${id}/activity/${actId}`, authH())
        .then(r => setContacts(prev => prev.map(c => c.id === id ? mapRel(r.data) : c)))
        .catch(e => console.error('Relationship activity delete failed', e.message));
    },
  };

  return (
    <div className="h-screen max-md:h-[calc(100dvh-3.5rem)] bg-gray-950 text-zinc-100 flex flex-col overflow-hidden max-md:overflow-x-hidden">
      <div className="px-8 max-md:px-4 pt-8 max-md:pt-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-3xl max-md:text-2xl font-bold tracking-tight">Contacts</h1>
          <button
            onClick={() => (tab === 'bids' ? setShowAddBid(true) : tab === 'relationships' ? setShowAdd(true) : setShowAddPerson(true))}
            className="px-6 py-3 bg-indigo-700/90 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg hover:scale-[1.02] flex items-center gap-2">
            <Plus className="w-4 h-4" /> {tab === 'bids' ? 'Add Bid' : tab === 'relationships' ? 'Add Relationship' : 'Add Contact'}
          </button>
        </div>

      </div>

      {/* Tab switcher lives beside each tab's search bar (passed down as a prop). */}
      {(() => {
        const tabBar = (
          <div className="flex gap-1 bg-gray-900/70 border border-gray-800 rounded-lg p-0.5 shrink-0">
            <TabBtn active={tab === 'people'} onClick={() => setTab('people')} icon={User} label="People" />
            <TabBtn active={tab === 'relationships'} onClick={() => setTab('relationships')} icon={Target} label="Relationships" />
            <TabBtn active={tab === 'bids'} onClick={() => setTab('bids')} icon={Briefcase} label="Bids & Jobs" />
          </div>
        );
        if (tab === 'relationships') return (
          <RelationshipsTab contacts={filteredContacts} allContacts={contacts} search={search} setSearch={setSearch}
            typeFilter={typeFilter} setTypeFilter={setTypeFilter} relTypes={relTypes} onOpen={setSelectedId} onSetStage={setRelStage} setContacts={setContacts} persistRel={patchRelServer} tabBar={tabBar} />
        );
        if (tab === 'people') return (
          <PeopleTab people={people} contacts={contacts} customers={customers} setCustomers={setCustomers} setPeople={setPeople} onOpenCompany={setSelectedId} onOpenPerson={setSelectedPersonId} onDeletePerson={deletePerson} tabBar={tabBar} />
        );
        return <BidsTab bids={bids} setBids={setBids} tabBar={tabBar} customers={customers} bidUploads={bidUploads}
          onPatchBid={patchBid} onAddBid={addBid} onPromoteBid={promoteBid} onDeleteBid={deleteBid}
          selectedBidId={openBidId}
          openBidSection={openBidSection}
          setSelectedBidId={(v) => { setOpenBidId(v); if (v === null) { setBidBackPersonId(null); setOpenBidSection('overview'); } }}
          backPerson={bidBackPersonId ? (customers.find(c => c.id === bidBackPersonId) || people.find(p => p.id === bidBackPersonId) || null) : null}
          onBackToPerson={backToPerson}
          onLogBidActivity={logBidActivity} onEditBidActivity={editBidActivity} onDeleteBidActivity={deleteBidActivity} />;
      })()}

      {selected && (
        <ContactDrawer contact={selected} people={[...people, ...customers]} onClose={() => setSelectedId(null)} onLog={logActivity} onUpdate={updateContact}
          onEditActivity={editActivity} onDeleteActivity={deleteActivity}
          onAddPerson={(companyName) => { setPersonPresetCompany(companyName); setShowAddPerson(true); }}
          onSetStage={setRelStage} onToggleStar={toggleStar} onOpenPerson={setSelectedPersonId} bids={bids} />
      )}
      {selectedPerson && (
        <PersonDrawer person={selectedPerson} contacts={contacts} onClose={() => setSelectedPersonId(null)} onLog={logPersonActivity} onUpdate={updatePerson}
          onEditActivity={editPersonActivity} onDeleteActivity={deletePersonActivity}
          onOpenBid={(bidId) => openBidFromPerson(bidId, selectedPersonId)}
          onOpenCompany={(cid) => { setSelectedPersonId(null); setSelectedId(cid); }} />
      )}
      {showAdd && <AddContactModal customers={customers} onClose={() => setShowAdd(false)} onAdd={async (c) => {
        try { await addRelationship(c); setShowAdd(false); }
        catch (e) { window.alert('Could not save the relationship: ' + (e.response?.data?.error || e.message)); }
      }} />}
      {showAddPerson && <AddPersonModal contacts={contacts} presetCompany={personPresetCompany}
        onClose={() => { setShowAddPerson(false); setPersonPresetCompany(''); }}
        onAdd={async (c) => {
          try {
            const typeMap = { 'Homeowner': 'Homeowner', 'Property Owner': 'Homeowner', 'Tenant': 'Other' };
            const res = await axios.post(`${API_BASE}/customers`, {
              name: c.name,
              nickname: c.nickname || '',
              businessName: c.company || '',
              primaryPhone: c.phone || '', secondaryPhone: c.phone2 || '',
              primaryEmail: c.email || '', altEmail: c.email2 || '',
              type: typeMap[c.role] || 'Other',
              status: ['Active', 'Lead', 'Vendor', 'Inactive', 'Lost'].includes(c.status) ? c.status : 'Lead',
              notes: [c.role, c.title].filter(Boolean).join(' · '),
            }, authH());
            setCustomers(prev => [mapCustomer(res.data), ...prev]);
            setShowAddPerson(false); setPersonPresetCompany('');
          } catch (e) {
            window.alert('Could not save the person: ' + (e.response?.data?.error || e.message));
          }
        }} />}
      {showAddBid && <AddBidModal customers={customers} contacts={contacts} people={people} sources={sources} setSources={setSources} onClose={() => setShowAddBid(false)}
        onAdd={addBid} onUploadPhotos={uploadBidPhotos} />}

      <style>{`
        /* End at transform:none (NOT translate(0)) — a lingering transform turns the
           drawer into a containing block that breaks position:fixed menus inside it. */
        @keyframes drawerIn { from { transform: translateX(24px); opacity: 0 } to { transform: none; opacity: 1 } }
        @keyframes fadeUp { from { transform: translateY(6px); opacity: 0 } to { transform: none; opacity: 1 } }
        .drawer-in { animation: drawerIn .28s cubic-bezier(.22,.61,.36,1) both }
        .fade-up { animation: fadeUp .35s ease both }
      `}</style>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label }) {
  return <button onClick={onClick} title={label} className={`px-3 py-1.5 rounded-md text-[13px] font-medium flex items-center gap-1.5 transition-all ${active ? 'bg-indigo-600 text-white shadow-sm' : 'text-zinc-400 hover:text-white'}`}><Icon className="w-3.5 h-3.5" /> <span className="max-md:hidden">{label}</span></button>;
}
// Remembered list/board preference (per view, per device).
const savedView = (key, fallback = 'list') => {
  try { const v = localStorage.getItem(key); if (v === 'list' || v === 'board') return v; } catch (_) {}
  return fallback;
};
const rememberView = (key, v) => { try { localStorage.setItem(key, v); } catch (_) {} };

// 9am local, N days out — used by the follow-up quick picks.
const followUpInDays = (d) => { const x = new Date(); x.setDate(x.getDate() + d); x.setHours(9, 0, 0, 0); return x.toISOString(); };
const FOLLOW_QUICK_PICKS = [
  { label: 'In 3 days', days: 3 }, { label: 'In 1 week', days: 7 },
  { label: 'In 2 weeks', days: 14 }, { label: 'In 1 month', days: 30 },
];

// Follow-up popover — quick picks, a custom date, or clear. onPick gets an ISO
// string or null (no follow-up). Rendered inside a `relative` wrapper.
function FollowUpMenu({ onPick, onClose, align = 'left' }) {
  const pick = (v) => { onPick(v); onClose(); };
  return (
    <>
      <span className="fixed inset-0 z-30" onClick={(e) => { e.stopPropagation(); onClose(); }} />
      <div onClick={(e) => e.stopPropagation()} className={`absolute z-40 top-7 ${align === 'right' ? 'right-0' : 'left-0'} bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-1.5 min-w-[11rem]`}>
        {FOLLOW_QUICK_PICKS.map(q => (
          <button key={q.days} onClick={() => pick(followUpInDays(q.days))}
            className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-gray-800 text-[12px] text-zinc-300">{q.label}</button>
        ))}
        <input type="date" onChange={e => { if (e.target.value) pick(new Date(e.target.value + 'T09:00:00').toISOString()); }}
          className="w-full mt-1 px-2.5 py-1.5 bg-gray-950 border border-gray-800 rounded-lg text-[12px] text-zinc-300 focus:outline-none focus:border-indigo-600/60" />
        <button onClick={() => pick(null)}
          className="w-full text-left px-2.5 py-1.5 mt-1 rounded-lg hover:bg-gray-800 text-[12px] text-zinc-500">No follow-up</button>
      </div>
    </>
  );
}

// Hover tooltip explaining what a relationship stage means.
function StageTip({ stage, children }) {
  const tip = (REL_STAGE_META[stage] || {}).tip;
  if (!tip) return children;
  return (
    <span className="relative group/tip inline-flex">
      {children}
      {/* z-[60] keeps the tip above the fixed SideNav (z-50) when the first column's tip overlaps it */}
      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1.5 px-2.5 py-1 rounded-lg bg-gray-800 border border-gray-700 text-[11px] font-medium normal-case tracking-normal text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-[60] shadow-xl">{tip}</span>
    </span>
  );
}
function StatCard({ icon: Icon, grad, value, label, tint, pulse }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-gray-800/60 bg-gradient-to-br ${grad} p-4`}>
      <div className="flex items-center justify-between">
        <span className={`text-2xl md:text-3xl font-bold ${tint}`}>{value}</span>
        <span className={`w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center ${tint} ${pulse ? 'animate-pulse' : ''}`}><Icon className="w-4 h-4" /></span>
      </div>
      <p className="text-zinc-400 text-xs mt-1 font-medium">{label}</p>
    </div>
  );
}

// ======================= RELATIONSHIPS =======================
function RelationshipsTab({ contacts, allContacts, search, setSearch, typeFilter, setTypeFilter, relTypes, onOpen, onSetStage, setContacts, persistRel = () => {}, tabBar }) {
  const [view, _setView] = useState(() => savedView('contacts.relView'));
  const setView = (v) => { _setView(v); rememberView('contacts.relView', v); };
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null); // { stage, index }

  // right-click delete — super admin only
  const isSuperAdmin = localStorage.getItem('role') === 'superadmin';
  const [relCtx, setRelCtx] = useState(null); // { id, company, x, y }
  const relContext = (c) => (e) => {
    if (!isSuperAdmin) return;
    e.preventDefault();
    setRelCtx({ id: c.id, company: c.company, x: e.clientX, y: e.clientY });
  };
  const confirmDeleteRel = async () => {
    const { id, company } = relCtx;
    setRelCtx(null);
    if (!window.confirm(`Delete relationship "${company}"? Its outreach history goes with it.`)) return;
    try {
      if (isMongoId(id)) await axios.delete(`${API_BASE}/relationships/${id}`, authH());
      setContacts(prev => prev.filter(c => c.id !== id));
    } catch (e) { window.alert('Delete failed: ' + (e.response?.data?.error || e.message)); }
  };

  // Move a contact card to a stage at a given index (drag-and-drop). Mirrors the
  // Bids board: changes relStage and reorders within the column.
  const moveContact = (id, toStage, toIndex) => setContacts(prev => {
    const dragged = prev.find(c => c.id === id);
    if (!dragged) return prev;
    if (dragged.relStage !== toStage) setTimeout(() => onSetStage(id, toStage), 0); // persist the stage change
    const without = prev.filter(c => c.id !== id);
    const updated = { ...dragged, relStage: toStage };
    const target = without.filter(c => c.relStage === toStage);
    const at = Math.max(0, Math.min(toIndex, target.length));
    const run = [...target.slice(0, at), updated, ...target.slice(at)];
    const result = []; let placed = false;
    for (const c of without) {
      if (c.relStage === toStage) { if (!placed) { result.push(...run); placed = true; } }
      else result.push(c);
    }
    if (!placed) result.push(...run);
    return result;
  });

  return (
    <>
      <div className="px-8 max-md:px-4 sticky top-0 z-20 bg-gray-950/85 backdrop-blur-md py-3 mt-5 border-y border-gray-800/50">
        <div className="flex items-center gap-3 max-md:flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search company, contact, address, tag…" className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60" />
          </div>
          {tabBar}
          <div className="flex bg-gray-900 border border-gray-800 rounded-xl p-1 shrink-0 ml-auto">
            <button onClick={() => setView('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${view === 'list' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white'}`}><ListIcon className="w-4 h-4" /><span className="max-md:hidden">List</span></button>
            <button onClick={() => setView('board')} className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${view === 'board' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white'}`}><LayoutGrid className="w-4 h-4" /><span className="max-md:hidden">Board</span></button>
          </div>
        </div>
      </div>

      <div className="px-8 max-md:px-4 py-6 flex-1 min-h-0 overflow-y-auto">
        {view === 'board' ? (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {REL_STAGES.map(stage => {
              const col = contacts.filter(c => c.relStage === stage);
              const m = REL_STAGE_META[stage];
              const overHere = dragOver && dragOver.stage === stage;
              return (
                <div key={stage} className="shrink-0 w-[300px] max-md:w-[82vw]">
                  <div className="flex items-center justify-between px-1 mb-3">
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: m.dot }} /><StageTip stage={stage}><h3 className="font-semibold text-sm text-zinc-200 cursor-help">{stage}</h3></StageTip></div>
                    <span className="text-xs font-bold text-zinc-500 bg-gray-900 border border-gray-800 rounded-full px-2 py-0.5">{col.length}</span>
                  </div>
                  <div className={`rounded-2xl bg-gradient-to-b ${m.bar} to-transparent p-[1px]`}>
                    <div
                      className={`rounded-2xl bg-gray-900/60 min-h-[120px] max-h-[calc(100dvh-320px)] overflow-y-auto p-2 space-y-2 transition-colors ${overHere ? 'ring-2 ring-indigo-500/40' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setDragOver({ stage, index: col.length }); }}
                      onDrop={(e) => { e.preventDefault(); if (dragId != null) moveContact(dragId, stage, overHere ? dragOver.index : col.length); setDragId(null); setDragOver(null); }}
                    >
                      {col.map((c, i) => (
                        <React.Fragment key={c.id}>
                          {overHere && dragOver.index === i && <DropLine />}
                          <div onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver({ stage, index: i }); }} onContextMenu={relContext(c)}>
                            <RelCard c={c} onOpen={onOpen} isDragging={dragId === c.id}
                              onDragStart={(e) => { setDragId(c.id); try { e.dataTransfer.setData('text/plain', c.id); } catch (_) {} }}
                              onDragEnd={() => { setDragId(null); setDragOver(null); }} />
                          </div>
                        </React.Fragment>
                      ))}
                      {overHere && dragOver.index >= col.length && <DropLine />}
                      {col.length === 0 && !overHere && <p className="text-center text-zinc-600 text-xs py-8">Empty</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : <RelListView contacts={contacts} onOpen={onOpen} onSetStage={onSetStage}
              onSetFollowUp={(id, iso) => { setContacts(prev => prev.map(c => c.id === id ? { ...c, nextFollowUp: iso } : c)); persistRel(id, { nextFollowUp: iso }); }}
              onRowContext={relContext}
              typeFilter={typeFilter} setTypeFilter={setTypeFilter} relTypes={relTypes} />}
        {contacts.length === 0 && <p className="text-center text-zinc-500 py-16">No relationships match your search.</p>}
      </div>

      {/* right-click menu — delete relationship (super admin only) */}
      {relCtx && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setRelCtx(null)} onContextMenu={(e) => { e.preventDefault(); setRelCtx(null); }} />
          <div className="fixed z-[70] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-1 min-w-[11rem]"
            style={{ left: Math.min(relCtx.x, (window.innerWidth || 800) - 190), top: Math.min(relCtx.y, (window.innerHeight || 600) - 60) }}>
            <button onClick={confirmDeleteRel} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-600/15 text-left text-sm text-red-400">
              <Trash2 className="w-3.5 h-3.5" /> Delete {relCtx.company}
            </button>
          </div>
        </>
      )}
    </>
  );
}

function RelCard({ c, onOpen, draggable = true, onDragStart, onDragEnd, isDragging }) {
  const last = c.activities?.[0];
  const LastIcon = last ? (CHANNEL_ICON[last.channel] || StickyNote) : null;
  const f = followLabel(c.nextFollowUp);
  return (
    <button
      onClick={() => onOpen(c.id)}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`w-full text-left bg-gray-900 hover:bg-gray-800/70 border border-gray-800/70 rounded-xl p-3 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/30 group cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-40' : ''}`}>
      <div className="flex items-start gap-2.5">
        <CompanyLogo contact={c} sizeClass="w-9 h-9" roundedClass="rounded-lg" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-zinc-100 truncate group-hover:text-white">{c.company}</p>
          <p className="text-[11px] text-zinc-500 truncate">{TYPE_META[c.type]} {c.contactName}</p>
        </div>
        {c.starred && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 shrink-0" />}
      </div>
      <div className="flex items-center justify-between mt-2 gap-2">
        {last ? <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500 min-w-0"><LastIcon className={`w-3.5 h-3.5 ${CHANNELS[last.channel]?.tone}`} /><span className="truncate">{timeAgo(last.at)}</span></span> : <span />}
        {f && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ring-1 ${f.tone}`}>{f.label}</span>}
      </div>
    </button>
  );
}

// Reusable click-to-pick chip with a colored-dot popover. Same UX as the People
// tab status pill, used across Relationships and Bids for a cohesive feel.
// `trigger` is the pill content; each option is { value, dot } where dot is a
// small JSX swatch. Stops propagation so it never triggers the row's onClick.
function InlinePicker({ triggerClass, trigger, options, current, onPick, minWidth = '10rem' }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={`cursor-pointer hover:ring-2 hover:ring-white/20 transition ${triggerClass}`}
        title="Click to change"
      >
        {trigger}
      </button>
      {open && (
        <>
          <span className="fixed inset-0 z-30" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div className="absolute z-40 top-8 left-0 bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-1 flex flex-col" style={{ minWidth }}>
            {options.map(o => (
              <button
                key={o.value}
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(false); onPick(o.value); }}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-800 text-left whitespace-nowrap"
              >
                {o.dot}
                <span className={`text-[12px] ${o.value === current ? 'text-white font-semibold' : 'text-zinc-300'}`}>{o.value}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}

function RelListView({ contacts, onOpen, onSetStage, onSetFollowUp, onRowContext, typeFilter, setTypeFilter, relTypes }) {
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [fuFor, setFuFor] = useState(null); // company id whose follow-up menu is open
  const ordered = [...contacts].sort((a, b) => REL_STAGES.indexOf(a.relStage) - REL_STAGES.indexOf(b.relStage));
  return (
    <div className="rounded-2xl border border-gray-800/60 bg-gray-900/40 overflow-hidden">
      <div className="max-md:hidden grid grid-cols-[1.5fr_1fr_0.9fr_1fr_0.9fr] gap-4 px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500 border-b border-gray-800/60 bg-[#0d1321]">
        <span>Company</span>
        <span className="relative inline-block">
          <button onClick={() => setTypeMenuOpen(o => !o)} className="uppercase tracking-wider inline-flex items-center gap-1 hover:text-zinc-300">
            Type{typeFilter !== 'All' && <span className="text-indigo-400 normal-case tracking-normal font-semibold"> · {typeFilter}</span>} <ChevronDown className="w-3 h-3" />
          </button>
          {typeMenuOpen && (
            <>
              <span className="fixed inset-0 z-30" onClick={() => setTypeMenuOpen(false)} />
              <div className="absolute z-40 top-6 left-0 bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-1 flex flex-col min-w-[14rem] normal-case tracking-normal">
                {relTypes.map(t => (
                  <button key={t} onClick={() => { setTypeFilter(t); setTypeMenuOpen(false); }} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-800 text-left">
                    <span className="w-4 text-center text-sm">{t === 'All' ? '' : TYPE_META[t]}</span>
                    <span className={`text-[12px] ${t === typeFilter ? 'text-white font-semibold' : 'text-zinc-300'}`}>{t === 'All' ? 'All types' : t}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </span>
        <span>Stage</span><span>Last activity</span><span>Follow-up</span>
      </div>
      <div className="divide-y divide-gray-800/50">
        {ordered.map(c => {
          const last = c.activities?.[0]; const LastIcon = last ? (CHANNEL_ICON[last.channel] || StickyNote) : null;
          const f = followLabel(c.nextFollowUp); const m = REL_STAGE_META[c.relStage];
          return (
            <div key={c.id} onClick={() => onOpen(c.id)} onContextMenu={onRowContext ? onRowContext(c) : undefined} className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_0.9fr_1fr_0.9fr] gap-2 md:gap-4 px-5 py-4 hover:bg-gray-800/40 cursor-pointer items-center group">
              <div className="flex items-center gap-3 min-w-0">
                <CompanyLogo contact={c} sizeClass="w-10 h-10" roundedClass="rounded-xl" />
                <div className="min-w-0">
                  <p className="font-semibold text-zinc-100 truncate group-hover:text-white flex items-center gap-1.5">{c.company}{c.starred && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 shrink-0" />}</p>
                  <p className="text-xs text-zinc-500 truncate">{c.contactName} · {c.title}</p>
                </div>
              </div>
              <div className="text-sm text-zinc-400 max-md:hidden min-w-0"><div className="truncate">{TYPE_META[c.type]} {c.type}</div></div>
              <div>
                <InlinePicker
                  current={c.relStage}
                  onPick={(s) => onSetStage(c.id, s)}
                  triggerClass={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${m.chip}`}
                  trigger={<><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.dot }} /> {c.relStage}</>}
                  options={REL_STAGES.map(s => ({ value: s, dot: <span className="w-2 h-2 rounded-full" style={{ backgroundColor: (REL_STAGE_META[s] || {}).dot || '#6b7280' }} /> }))}
                />
              </div>
              <div className="text-sm text-zinc-400 max-md:hidden">{last ? <span className="inline-flex items-center gap-1.5"><LastIcon className={`w-4 h-4 ${CHANNELS[last.channel]?.tone}`} /><span className="capitalize">{last.channel}</span><span className="text-zinc-600">·</span><span className="text-zinc-500">{timeAgo(last.at)}</span></span> : <span className="text-zinc-600">—</span>}</div>
              <div className="relative">
                <button onClick={(e) => { e.stopPropagation(); setFuFor(fuFor === c.id ? null : c.id); }} title="Click to change follow-up"
                  className={`text-[11px] font-bold px-2 py-0.5 rounded ring-1 hover:ring-white/30 ${f ? f.tone : 'bg-gray-900 text-zinc-600 ring-gray-800'}`}>
                  {f ? f.label : '—'}
                </button>
                {fuFor === c.id && <FollowUpMenu align="right" onPick={(iso) => onSetFollowUp(c.id, iso)} onClose={() => setFuFor(null)} />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ======================= PEOPLE / DIRECTORY (unified, Customers-style list) =======================
const STATUS_FROM_REL = { 'Active Source': 'Active Client', 'Pursuing': 'In Talks', 'Prospect': 'Prospect', 'Dormant': 'Inactive', 'Not a Fit': 'Inactive' };
const DIR_STATUS_COLORS = {
  // real customer statuses (kept as-is)
  'Active': 'bg-emerald-600/80 text-white', 'Lead': 'bg-yellow-600/80 text-white',
  'Inactive': 'bg-gray-600/80 text-white', 'Lost': 'bg-red-600/80 text-white', 'Vendor': 'bg-blue-600/80 text-white',
  // non-customer contacts
  'Contact': 'bg-zinc-700/60 text-zinc-300', '3rd-party': 'bg-amber-600/70 text-white',
};

// Status options offered when you click a status chip, by row kind.
const CUSTOMER_STATUSES = ['Active', 'Lead', 'Inactive', 'Lost', 'Vendor'];
const PERSON_STATUSES = ['Contact', '3rd-party', 'Vendor', 'Inactive'];

function PeopleTab({ people, contacts, customers, setCustomers, setPeople, onOpenCompany, onOpenPerson, onDeletePerson, tabBar }) {
  // right-click delete — super admin only
  const isSuperAdmin = localStorage.getItem('role') === 'superadmin';
  const [rowCtx, setRowCtx] = useState(null); // { r, x, y }
  const rowContext = (r) => (e) => {
    if (!isSuperAdmin) return;
    e.preventDefault();
    setRowCtx({ r, x: e.clientX, y: e.clientY });
  };
  const confirmDeletePerson = async () => {
    const { r } = rowCtx;
    setRowCtx(null);
    const warn = r.kind === 'customer'
      ? `Delete customer "${r.name}"? This removes them from the portal entirely — projects and paperwork that reference them keep their own copies, but this can't be undone.`
      : `Delete "${r.name}"?`;
    if (!window.confirm(warn)) return;
    try { await onDeletePerson(r); }
    catch (e) { window.alert('Delete failed: ' + (e.response?.data?.error || e.message)); }
  };
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [statusMenuOpen, setStatusMenuOpen] = useState(false); // Status column header filter dropdown
  const [copied, setCopied] = useState(null); // { id, field }
  const [statusOpen, setStatusOpen] = useState(null); // row id whose status menu is open
  const companyOf = (p) => p.companyId ? ((contacts.find(c => c.id === p.companyId) || {}).company || '') : (p.companyName || '');

  // People = individuals only (homeowner customers + contacts). Companies live in the Relationships tab.
  const rows = useMemo(() => ([
    ...customers.map(c => ({ kind: 'customer', id: c.id, name: c.name, type: c.type, role: c.role || '', company: c.company || '', status: c.status, phone: c.phone, email: c.email, city: c.city || '' })),
    ...people.map(p => ({ kind: 'person', id: p.id, name: p.name, type: p.role, role: p.role || '', company: companyOf(p), status: p.status || (p.companyId ? 'Contact' : '3rd-party'), phone: p.phone, email: p.email, city: p.city || '' })),
  ]), [people, contacts, customers]);

  // Click a status chip → pick a new one. Looks like a chip; acts like a control.
  // Real customers persist to the portal DB; the example row stays local.
  const setStatus = (r, status) => {
    if (r.kind === 'customer') {
      setCustomers(prev => prev.map(c => c.id === r.id ? { ...c, status } : c));
      if (isMongoId(r.id)) {
        axios.put(`${API_BASE}/customers/${r.id}`, { status }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
          .catch(e => console.error('Customer status update failed', e.message));
      }
    } else setPeople(prev => prev.map(p => p.id === r.id ? { ...p, status } : p));
    setStatusOpen(null);
  };
  const StatusChip = ({ r }) => {
    const opts = r.kind === 'customer' ? CUSTOMER_STATUSES : PERSON_STATUSES;
    const key = r.kind + r.id;
    const open = statusOpen && statusOpen.key === key;
    return (
      <span data-copy className="relative inline-block">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (open) { setStatusOpen(null); return; }
            // Fixed-position menu so the table's overflow-hidden can't clip it;
            // opens upward when there isn't room below (bottom rows).
            const rect = e.currentTarget.getBoundingClientRect();
            const menuH = opts.length * 30 + 12;
            const up = rect.bottom + menuH > window.innerHeight - 8;
            setStatusOpen({ key, x: rect.left, y: up ? rect.top - menuH - 4 : rect.bottom + 4 });
          }}
          className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full transition-shadow hover:ring-2 hover:ring-white/20 ${DIR_STATUS_COLORS[r.status] || 'bg-gray-600/80 text-white'}`}
          title="Click to change status"
        >{r.status}</button>
        {open && (
          <>
            <span className="fixed inset-0 z-30" onClick={(e) => { e.stopPropagation(); setStatusOpen(null); }} />
            <span className="fixed z-40 bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-1 flex flex-col min-w-[7rem]"
              style={{ left: Math.min(statusOpen.x, (window.innerWidth || 800) - 130), top: Math.max(8, statusOpen.y) }}>
              {opts.map(s => (
                <button key={s} onClick={(e) => { e.stopPropagation(); setStatus(r, s); }} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-800 text-left">
                  <span className={`w-2 h-2 rounded-full ${(DIR_STATUS_COLORS[s] || 'bg-gray-500').split(' ')[0]}`} />
                  <span className={`text-[12px] ${s === r.status ? 'text-white font-semibold' : 'text-zinc-300'}`}>{s}</span>
                </button>
              ))}
            </span>
          </>
        )}
      </span>
    );
  };

  const statuses = ['All', ...Array.from(new Set(rows.map(r => r.status)))];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== 'All' && r.status !== statusFilter) return false;
      if (!q) return true;
      return [r.name, r.nickname, r.type, r.role, r.company, r.phone, r.email, r.city].some(v => String(v || '').toLowerCase().includes(q));
    });
  }, [rows, search, statusFilter]);

  const open = (r) => { onOpenPerson(r.id); }; // opens the detail drawer for both contacts and customers (e.g. homeowners)
  const copy = async (text, id, field) => {
    try { await navigator.clipboard.writeText(text); setCopied({ id, field }); setTimeout(() => setCopied(null), 1500); } catch (_) {}
  };
  // Outer span stays overflow-visible so the "Copied!" bubble shows; the inner
  // span carries any truncate/ellipsis class without clipping the bubble.
  const Copyable = ({ r, field, value, className }) => (
    <span data-copy className="relative inline-block max-w-full align-bottom">
      <span
        onClick={(e) => { e.stopPropagation(); if (value) copy(value, r.id, field); }}
        className={`cursor-pointer hover:text-white inline-block max-w-full ${className || ''}`}
        title="Click to copy"
      >{value || '—'}</span>
      {copied?.id === r.id && copied?.field === field && (
        <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-100 text-[10px] px-2 py-0.5 rounded shadow z-20 whitespace-nowrap">Copied!</span>
      )}
    </span>
  );

  return (
    <>
      <div className="px-8 max-md:px-4 sticky top-0 z-20 bg-gray-950/85 backdrop-blur-md py-3 mt-5 border-y border-gray-800/50">
        <div className="flex items-center gap-3 max-md:flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, type, phone, email, city…" className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60" />
          </div>
          {tabBar}
        </div>
      </div>

      <div className="px-8 max-md:px-4 py-6 flex-1 min-h-0 overflow-y-auto">
        {/* desktop table */}
        <div className="max-md:hidden rounded-2xl border border-gray-800/60 bg-gray-900/40 overflow-hidden">
          <div className="grid grid-cols-[1.5fr_1fr_1.3fr_0.9fr_1fr_1.4fr] gap-4 px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500 border-b border-gray-800/60 bg-[#0d1321]">
            <span>Name</span><span>Type</span><span>Company</span>
            <span className="relative inline-block">
              <button
                type="button"
                onClick={() => setStatusMenuOpen(o => !o)}
                className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-zinc-300"
                title="Filter by status"
              >
                Status
                {statusFilter !== 'All' && <span className="normal-case font-semibold text-indigo-300">· {statusFilter}</span>}
                <ChevronDown className="w-3 h-3" />
              </button>
              {statusMenuOpen && (
                <>
                  <span className="fixed inset-0 z-30" onClick={() => setStatusMenuOpen(false)} />
                  <div className="absolute z-40 top-6 left-0 bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-1 flex flex-col min-w-[9rem] normal-case tracking-normal">
                    {statuses.map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => { setStatusFilter(s); setStatusMenuOpen(false); }}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-800 text-left"
                      >
                        <span className={`w-2 h-2 rounded-full ${s === 'All' ? 'bg-zinc-500' : (DIR_STATUS_COLORS[s] || 'bg-gray-500').split(' ')[0]}`} />
                        <span className={`text-[12px] font-medium ${s === statusFilter ? 'text-white' : 'text-zinc-300'}`}>{s}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </span>
            <span>Phone</span><span>Email</span>
          </div>
          <div className="divide-y divide-gray-800/50">
            {filtered.map(r => (
              <div key={r.kind + r.id} onClick={(e) => { if (!e.target.closest('[data-copy]')) open(r); }} onContextMenu={rowContext(r)} className="grid grid-cols-[1.5fr_1fr_1.3fr_0.9fr_1fr_1.4fr] gap-4 px-5 py-4 hover:bg-gray-800/40 cursor-pointer items-center text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <Copyable r={r} field="name" value={r.name} className="text-zinc-100 truncate" />{r.nickname && <span className="text-zinc-500 text-xs shrink-0">“{r.nickname}”</span>}
                </div>
                <span className="text-zinc-400 truncate">{r.type !== 'Homeowner' && TYPE_META[r.type] ? `${TYPE_META[r.type]} ` : ''}{r.type}</span>
                <Copyable r={r} field="company" value={r.company} className="text-zinc-500 truncate" />
                <div><StatusChip r={r} /></div>
                <Copyable r={r} field="phone" value={fmtPhone(r.phone)} className="text-zinc-300" />
                <Copyable r={r} field="email" value={r.email} className="text-zinc-300 truncate" />
              </div>
            ))}
          </div>
        </div>

        {/* mobile cards */}
        <div className="md:hidden space-y-2.5">
          {filtered.map(r => (
            <div key={r.kind + r.id} onClick={(e) => { if (!e.target.closest('[data-copy]')) open(r); }} onContextMenu={rowContext(r)} className="bg-gray-900 border border-gray-800/60 rounded-2xl p-4 active:bg-gray-800/70">
              <div className="flex items-center justify-between gap-2 mb-1">
                <Copyable r={r} field="name" value={r.name} className="text-zinc-100 min-w-0 break-words" />{r.nickname && <span className="text-zinc-500 text-xs">“{r.nickname}”</span>}
                <StatusChip r={r} />
              </div>
              <p className="text-zinc-500 text-xs">{r.type !== 'Homeowner' && TYPE_META[r.type] ? `${TYPE_META[r.type]} ` : ''}{r.type}{r.company ? ' · ' : ''}{r.company && <Copyable r={r} field="company" value={r.company} className="text-zinc-400" />}</p>
              <div className="mt-1.5 text-sm"><Copyable r={r} field="phone" value={fmtPhone(r.phone)} className="text-indigo-400" /></div>
              <div className="text-sm"><Copyable r={r} field="email" value={r.email} className="text-zinc-400 break-all" /></div>
              {r.city && <p className="text-zinc-600 text-xs mt-1">{r.city}</p>}
            </div>
          ))}
        </div>

        {filtered.length === 0 && <p className="text-center text-zinc-500 py-16">No one matches your search.</p>}
      </div>

      {/* right-click menu — delete person/customer (super admin only) */}
      {rowCtx && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setRowCtx(null)} onContextMenu={(e) => { e.preventDefault(); setRowCtx(null); }} />
          <div className="fixed z-[70] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-1 min-w-[11rem]"
            style={{ left: Math.min(rowCtx.x, (window.innerWidth || 800) - 190), top: Math.min(rowCtx.y, (window.innerHeight || 600) - 60) }}>
            <button onClick={confirmDeletePerson} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-600/15 text-left text-sm text-red-400">
              <Trash2 className="w-3.5 h-3.5" /> Delete {rowCtx.r.name}
            </button>
          </div>
        </>
      )}
    </>
  );
}

// ======================= BIDS =======================
function BidsTab({ bids, setBids, tabBar, customers, bidUploads, onPatchBid, onAddBid, onPromoteBid, onDeleteBid, onLogBidActivity, onEditBidActivity, onDeleteBidActivity, selectedBidId, setSelectedBidId, openBidSection, backPerson, onBackToPerson }) {
  // right-click delete — super admin only
  const isSuperAdmin = localStorage.getItem('role') === 'superadmin';
  const [bidCtx, setBidCtx] = useState(null); // { id, title, x, y }
  const bidContext = (b) => (e) => {
    if (!isSuperAdmin) return;
    e.preventDefault();
    setBidCtx({ id: b.id, title: b.title, x: e.clientX, y: e.clientY });
  };
  const confirmDeleteBid = async () => {
    const { id, title } = bidCtx;
    setBidCtx(null);
    if (!window.confirm(`Delete bid "${title}"?\n\nThis permanently removes the bid, its activity log, AND its staged photo/file folder from this year's /Bids in Dropbox.`)) return;
    try { await onDeleteBid(id); }
    catch (e) { window.alert('Delete failed: ' + (e.response?.data?.error || e.message)); }
  };
  const [view, _setView] = useState(() => savedView('contacts.bidsView'));
  const setView = (v) => { _setView(v); rememberView('contacts.bidsView', v); };
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null); // { stage, index }
  const [search, setSearch] = useState('');
  const selectedBid = bids.find(b => b.id === selectedBidId) || null;

  // Same search behavior as People/Relationships — matches across the fields
  // you'd actually hunt by. Empty query shows everything.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bids;
    return bids.filter(b =>
      [b.title, b.who, b.type, b.source, b.stage, b.detail?.address].some(v => String(v || '').toLowerCase().includes(q))
    );
  }, [bids, search]);

  // Winning IS the promotion — flipping a real, unpromoted bid to Won asks
  // once, then creates the customer (if needed), project, PO, and folders.
  const setStage = (id, stage) => {
    const b = bids.find(x => x.id === id);
    if (stage === 'Won' && b && b._real && !b.project) {
      if (!window.confirm(`Mark "${b.title}" as Won and create the project?\n\nThis assigns the next PO number, creates the customer record if needed, builds the Dropbox folders, and moves staged files into them.`)) return;
      onPromoteBid(id)
        .then(r => window.alert(`Won! Created project PO ${r.project.poNumber}.${r.moveFailures ? `\n\nWarning: ${r.moveFailures} file(s) could not be moved — the /Bids folder was kept so nothing is lost.` : ''}`))
        .catch(e => window.alert('Could not create the project: ' + (e.response?.data?.error || e.message)));
      return;
    }
    onPatchBid(id, { stage });
  };
  const setSource = (id, source) => onPatchBid(id, { source });
  const moveBid = (id, toStage, toIndex) => {
    const dragged0 = bids.find(b => b.id === id);
    // Dragging into the Won column goes through the same confirm-and-create.
    if (dragged0 && toStage === 'Won' && dragged0.stage !== 'Won' && dragged0._real && !dragged0.project) {
      setStage(id, 'Won');
      return;
    }
    setBids(prev => {
    const dragged = prev.find(b => b.id === id);
    if (!dragged) return prev;
    if (dragged.stage !== toStage) onPatchBid(id, { stage: toStage }); // persist the stage change
    const without = prev.filter(b => b.id !== id);
    const updated = { ...dragged, stage: toStage };
    const target = without.filter(b => b.stage === toStage);
    const at = Math.max(0, Math.min(toIndex, target.length));
    const run = [...target.slice(0, at), updated, ...target.slice(at)];
    const result = []; let placed = false;
    for (const b of without) {
      if (b.stage === toStage) { if (!placed) { result.push(...run); placed = true; } }
      else result.push(b);
    }
    if (!placed) result.push(...run);
    return result;
    });
  };
  return (
    <>
      <div className="px-8 max-md:px-4 sticky top-0 z-20 bg-gray-950/85 backdrop-blur-md py-3 mt-5 border-y border-gray-800/50">
        <div className="flex items-center gap-3 max-md:flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search job, company, source…" className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60" />
          </div>
          {tabBar}
          <div className="flex bg-gray-900 border border-gray-800 rounded-xl p-1 ml-auto shrink-0">
            <button onClick={() => setView('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${view === 'list' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white'}`}><ListIcon className="w-4 h-4" /><span className="max-md:hidden">List</span></button>
            <button onClick={() => setView('board')} className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${view === 'board' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white'}`}><LayoutGrid className="w-4 h-4" /><span className="max-md:hidden">Board</span></button>
          </div>
        </div>
      </div>

      <div className="px-8 max-md:px-4 py-5 flex-1 min-h-0 overflow-y-auto">
        {view === 'board' ? (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {BID_STAGES.map(stage => {
              const col = visible.filter(b => b.stage === stage); const m = BID_STAGE_META[stage];
              const colValue = col.reduce((s, b) => s + b.value, 0);
              const overHere = dragOver && dragOver.stage === stage;
              return (
                <div key={stage} className="shrink-0 w-[300px] max-md:w-[82vw]">
                  <div className="flex items-center justify-between px-1 mb-3"><div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: m.dot }} /><h3 className="font-semibold text-sm text-zinc-200">{stage}</h3></div><span className="text-[11px] font-bold text-zinc-500">{moneyK(colValue)}</span></div>
                  <div className={`rounded-2xl bg-gradient-to-b ${m.bar} to-transparent p-[1px]`}>
                    <div
                      className={`rounded-2xl bg-gray-900/60 min-h-[120px] max-h-[calc(100dvh-320px)] overflow-y-auto p-2 space-y-2 transition-colors ${overHere ? 'ring-2 ring-indigo-500/40' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setDragOver({ stage, index: col.length }); }}
                      onDrop={(e) => { e.preventDefault(); if (dragId != null) moveBid(dragId, stage, overHere ? dragOver.index : col.length); setDragId(null); setDragOver(null); }}
                    >
                      {col.map((b, i) => (
                        <React.Fragment key={b.id}>
                          {overHere && dragOver.index === i && <DropLine />}
                          <div onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver({ stage, index: i }); }} onContextMenu={bidContext(b)}>
                            <BidCard b={b} onOpen={setSelectedBidId} isDragging={dragId === b.id} upload={bidUploads && bidUploads[b.id]}
                              onDragStart={(e) => { setDragId(b.id); try { e.dataTransfer.setData('text/plain', b.id); } catch (_) {} }}
                              onDragEnd={() => { setDragId(null); setDragOver(null); }} />
                          </div>
                        </React.Fragment>
                      ))}
                      {overHere && dragOver.index >= col.length && <DropLine />}
                      {col.length === 0 && !overHere && <p className="text-center text-zinc-600 text-xs py-8">Empty</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : <BidListView bids={visible} onOpen={setSelectedBidId} onSetStage={setStage} onSetSource={setSource} onRowContext={bidContext} />}
        {visible.length === 0 && <p className="text-center text-zinc-500 py-16">No bids match your search.</p>}
      </div>

      {/* right-click menu — delete bid (super admin only) */}
      {bidCtx && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setBidCtx(null)} onContextMenu={(e) => { e.preventDefault(); setBidCtx(null); }} />
          <div className="fixed z-[70] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-1 min-w-[10rem]"
            style={{ left: Math.min(bidCtx.x, (window.innerWidth || 800) - 180), top: Math.min(bidCtx.y, (window.innerHeight || 600) - 60) }}>
            <button onClick={confirmDeleteBid} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-600/15 text-left text-sm text-red-400">
              <Trash2 className="w-3.5 h-3.5" /> Delete bid
            </button>
          </div>
        </>
      )}

      {selectedBid && <BidDrawer bid={selectedBid} onClose={() => setSelectedBidId(null)}
        onPatch={onPatchBid} onPromote={onPromoteBid} initialSection={openBidSection}
        backPerson={backPerson} onBackToPerson={onBackToPerson}
        onLog={onLogBidActivity} onEditActivity={onEditBidActivity} onDeleteActivity={onDeleteBidActivity} />}
    </>
  );
}

function DropLine() {
  return <div className="h-7 rounded-lg border-2 border-dashed border-indigo-400/70 bg-indigo-500/5" />;
}

// Small Dropbox-generated thumbnail (same /media/thumbnail proxy the project
// gallery uses — tiny JPEG, browser-cached a day, HEIC and video friendly).
function BidThumb({ path, alt, className, fallback }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true; let url = null;
    setSrc(null); setFailed(false);
    axios.get(`${API_BASE}/media/thumbnail?path=${encodeURIComponent(path)}&size=w256h256`,
      { ...authH(), responseType: 'blob' })
      .then(r => { if (!alive) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (src) return <img src={src} alt={alt || ''} loading="lazy" className={className} />;
  return (
    <span className="w-full h-full flex items-center justify-center">
      {failed ? fallback : <ImageIcon className="w-5 h-5 text-zinc-700 animate-pulse" />}
    </span>
  );
}

function BidCard({ b, onOpen, draggable = true, onDragStart, onDragEnd, isDragging, upload }) {
  const f = followLabel(b.due);
  const margin = b.detail?.estCost ? Math.round(((b.value - b.detail.estCost) / b.value) * 100) : null;
  // background photo upload → thin progress border along the card's bottom
  const upPct = upload ? Math.round(((upload.done + upload.pct / 100) / upload.total) * 100) : null;
  return (
    <button
      onClick={() => onOpen(b.id)}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`relative overflow-hidden w-full text-left bg-gray-900 border border-gray-800/70 rounded-xl p-3 hover:bg-gray-800/60 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/30 transition-all group cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className="flex items-start justify-between gap-2"><p className="text-sm font-semibold text-zinc-100 leading-snug group-hover:text-white">{b.title}</p><span className="text-sm font-bold text-emerald-300 shrink-0">{moneyK(b.value)}</span></div>
      <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{TYPE_META[b.type]} {b.who}</p>
      <div className="flex items-center justify-between mt-2.5 gap-2"><div className="flex items-center gap-1.5 min-w-0"><SourceTag source={b.source} />{b.quotedPrice > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 shrink-0" title={b.quotedNote || 'Verbal quote'}>Quoted: {moneyK(b.quotedPrice)}</span>}</div><div className="flex items-center gap-1.5">{margin != null && <span className="text-[10px] font-bold text-zinc-500">{margin}% margin</span>}{f && b.stage === 'Bidding' && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ring-1 ${f.tone}`}>{f.label}</span>}</div></div>
      {upPct != null && (
        <>
          <span className="block mt-2 text-[9px] font-semibold text-indigo-300 animate-pulse leading-none">
            Media Uploading… {upload.done + 1}/{upload.total}
          </span>
          <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-gray-800">
            <span className="block h-full bg-indigo-500 transition-all duration-200" style={{ width: `${upPct}%` }} />
          </span>
        </>
      )}
    </button>
  );
}

function BidListView({ bids, onOpen, onSetStage, onSetSource, onRowContext }) {
  const ordered = [...bids].sort((a, b) => BID_STAGES.indexOf(a.stage) - BID_STAGES.indexOf(b.stage));
  const [copied, setCopied] = useState(null); // bid id whose client was copied
  const copyClient = async (e, b) => {
    e.stopPropagation();
    if (!b.who) return;
    try { await navigator.clipboard.writeText(b.who); setCopied(b.id); setTimeout(() => setCopied(null), 1500); } catch (_) {}
  };
  return (
    <div className="rounded-2xl border border-gray-800/60 bg-gray-900/40 overflow-hidden">
      <div className="max-md:hidden grid grid-cols-[1.6fr_1fr_0.8fr_0.9fr_0.8fr] gap-4 px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500 border-b border-gray-800/60 bg-[#0d1321]"><span>Job</span><span>Client</span><span>Stage</span><span>Source</span><span className="text-right">Value</span></div>
      <div className="divide-y divide-gray-800/50">
        {ordered.map(b => {
          const m = BID_STAGE_META[b.stage]; const f = followLabel(b.due);
          return (
            <div key={b.id} onClick={() => onOpen(b.id)} onContextMenu={onRowContext ? onRowContext(b) : undefined} className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr_0.8fr_0.9fr_0.8fr] gap-2 md:gap-4 px-5 py-4 hover:bg-gray-800/40 cursor-pointer items-center group">
              <div className="min-w-0"><p className="font-semibold text-zinc-100 truncate group-hover:text-white">{b.title}</p>
                <p className="text-xs text-zinc-500 md:hidden">{TYPE_META[b.type]} <span onClick={(e) => copyClient(e, b)} className="relative cursor-pointer hover:text-zinc-300 inline-block" title="Click to copy client">{b.who}
                  {copied === b.id && <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-100 text-[10px] px-2 py-0.5 rounded shadow z-20 whitespace-nowrap">Copied!</span>}
                </span></p></div>
              <div className="text-sm text-zinc-400 max-md:hidden">{TYPE_META[b.type]} <span onClick={(e) => copyClient(e, b)} className="relative cursor-pointer hover:text-white inline-block" title="Click to copy client">{b.who}
                {copied === b.id && <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-100 text-[10px] px-2 py-0.5 rounded shadow z-20 whitespace-nowrap">Copied!</span>}
              </span></div>
              <div>
                <InlinePicker
                  current={b.stage}
                  onPick={(s) => onSetStage(b.id, s)}
                  triggerClass={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${m.chip}`}
                  trigger={<><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.dot }} /> {b.stage}</>}
                  options={BID_STAGES.map(s => ({ value: s, dot: <span className="w-2 h-2 rounded-full" style={{ backgroundColor: (BID_STAGE_META[s] || {}).dot || '#6b7280' }} /> }))}
                />
              </div>
              <div className="max-md:hidden flex items-center gap-2">
                <InlinePicker
                  current={b.source}
                  onPick={(s) => onSetSource(b.id, s)}
                  triggerClass={`text-[10px] font-bold px-1.5 py-0.5 rounded ${SOURCE_TONE[b.source] || 'bg-zinc-700/40 text-zinc-300'}`}
                  trigger={b.source}
                  minWidth="9rem"
                  options={SOURCES.map(s => {
                    const dotClass = ((SOURCE_TONE[s] || 'bg-zinc-700/40 text-zinc-300').split(' ')[1] || 'text-zinc-300').replace('text-', 'bg-');
                    return { value: s, dot: <span className={`w-2 h-2 rounded-full ${dotClass}`} /> };
                  })}
                />
                {f && b.stage === 'Bidding' && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ring-1 ${f.tone}`}>{f.label}</span>}
                {b.quotedPrice > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300" title={b.quotedNote || 'Verbal quote'}>Quoted: {moneyK(b.quotedPrice)}</span>}
              </div>
              <div className="text-right font-bold text-emerald-300">{moneyK(b.value)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- bid drawer ----
const DOC_TONE = { Contract: 'bg-emerald-500/15 text-emerald-300', Proposal: 'bg-indigo-500/15 text-indigo-300', Invoice: 'bg-amber-500/15 text-amber-300', Permit: 'bg-sky-500/15 text-sky-300', COI: 'bg-violet-500/15 text-violet-300', Plans: 'bg-cyan-500/15 text-cyan-300', RFP: 'bg-zinc-500/15 text-zinc-300', RFI: 'bg-zinc-500/15 text-zinc-300' };
function BidDrawer({ bid, onClose, onPatch, onPromote, onLog, onEditActivity, onDeleteActivity, backPerson, onBackToPerson, initialSection }) {
  const d = bid.detail; const [section, setSection] = useState(initialSection || 'overview');
  const isReal = !!bid._real;
  const navigate = useNavigate();

  // ---- edit mode (real bids) ----
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const openEdit = () => { setForm({
    title: bid.title, value: bid.value || 0, source: bid.source || '',
    due: bid.due ? String(bid.due).slice(0, 10) : '', notes: bid.notes || '',
    street: bid.jobSiteAddress?.street || '', city: bid.jobSiteAddress?.city || '', zip: bid.jobSiteAddress?.zip || '',
  }); setEditing(true); };
  const saveEdit = () => {
    onPatch(bid.id, {
      title: form.title.trim() || bid.title, value: Number(form.value) || 0, source: form.source,
      due: form.due ? new Date(form.due + 'T09:00:00').toISOString() : null, notes: form.notes,
      jobSiteAddress: { ...(bid.jobSiteAddress || {}), street: form.street, city: form.city, zip: form.zip },
    });
    setEditing(false);
  };

  // ---- quotes attached to this bid ----
  const [quotes, setQuotes] = useState([]);
  const [bidProposals, setBidProposals] = useState([]);
  useEffect(() => {
    if (!isReal) return;
    axios.get(`${API_BASE}/quotes`, authH())
      .then(r => setQuotes((r.data || []).filter(q => String(q.bid?._id || q.bid) === String(bid.id))))
      .catch(() => {});
    axios.get(`${API_BASE}/proposals?bid=${bid.id}`, authH())
      .then(r => setBidProposals(r.data || []))
      .catch(() => {});
    // Invoices born from this paper (sourceQuote/sourceProposal lineage) —
    // powers the INV · Sent/Paid chips on the rows.
    axios.get(`${API_BASE}/invoices`, authH())
      .then(r => setLinkedInvoices((r.data || []).filter(i => (i.sourceQuote || i.sourceProposal) && i.status !== 'Revised')))
      .catch(() => {});
  }, [bid.id, isReal]);
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const invoiceOf = (q) => linkedInvoices.find(i => q._kind === 'proposal'
    ? String(i.sourceProposal || '') === String(q._id)
    : String(i.sourceQuote || '') === String(q._id));
  // quote status pill → picker (Draft/Sent/Viewed/Accepted/Rejected/Revised)
  const QUOTE_STATUSES = ['Draft', 'Sent', 'Viewed', 'Accepted', 'Rejected', 'Revised'];
  const QS_TONE = { Draft: 'bg-zinc-700/50 text-zinc-300', Sent: 'bg-indigo-500/20 text-indigo-300', Viewed: 'bg-sky-500/20 text-sky-300', Accepted: 'bg-emerald-500/20 text-emerald-300', Rejected: 'bg-red-500/20 text-red-300', Revised: 'bg-purple-500/20 text-purple-300' };
  const [qsMenu, setQsMenu] = useState(null); // { id, x, y }
  const setPaperStatus = (id, status, kind) => {
    if (kind === 'proposal') {
      setBidProposals(prev => prev.map(q => q._id === id ? { ...q, status } : q));
      axios.put(`${API_BASE}/proposals/${id}`, { status }, authH()).catch(e => console.error('Proposal status failed', e.message));
      return;
    }
    setQuoteStatus(id, status);
  };
  const setQuoteStatus = (id, status) => {
    setQsMenu(null);
    setQuotes(prev => prev.map(q => q._id === id ? { ...q, status } : q));
    axios.put(`${API_BASE}/quotes/${id}`, { status }, authH()).catch(e => console.error('Quote status failed', e.message));
  };

  // ---- verbal quote: a price given over a call/text, no formal paper ----
  const [vqOpen, setVqOpen] = useState(false);
  const [vqPrice, setVqPrice] = useState('');
  const [vqNote, setVqNote] = useState('');
  const openVq = () => { setVqPrice(bid.quotedPrice != null ? String(bid.quotedPrice) : ''); setVqNote(bid.quotedNote || ''); setVqOpen(true); };
  const saveVq = () => {
    const n = Number(vqPrice);
    if (!n || n <= 0) return;
    onPatch(bid.id, { quotedPrice: n, quotedNote: vqNote.trim(), quotedAt: new Date().toISOString() });
    setVqOpen(false);
  };
  const removeVq = () => {
    if (!window.confirm('Remove this verbal quote?')) return;
    onPatch(bid.id, { quotedPrice: null, quotedNote: '', quotedAt: null });
  };

  // ---- files (real bids; staged under /{year}/Bids/…) ----
  const [files, setFiles] = useState(null); // { images, videos, documents } | null
  const [uploading, setUploading] = useState('');
  const [fileView, setFileView] = useState('grid'); // 'grid' | 'list' — grid default, like project media
  // right-click → delete a staged file (super admin only)
  const isSuperAdminViewer = localStorage.getItem('role') === 'superadmin';
  const [fileCtx, setFileCtx] = useState(null); // { path, name, x, y }
  const fileContext = (file) => (e) => {
    if (!isSuperAdminViewer) return;
    e.preventDefault();
    setFileCtx({ path: file.path, name: file.name, x: e.clientX, y: e.clientY });
  };
  const confirmDeleteFile = async () => {
    const { path, name } = fileCtx;
    setFileCtx(null);
    if (!window.confirm(`Delete "${name}" from this bid? This removes the file from Dropbox for good.`)) return;
    try {
      await axios.delete(`${API_BASE}/bids/${bid.id}/file`, { data: { path }, headers: { Authorization: localStorage.getItem('token') } });
      loadFiles();
    } catch (e) { window.alert('Delete failed: ' + (e.response?.data?.error || e.message)); }
  };
  // Image tiles open the shared gallery lightbox (zoom/pan) — not a download.
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const isHeicName = (n) => /\.hei[cf]$/i.test(n || '');
  const lightboxImages = (files?.images || []).map(file => ({
    name: file.name,
    getUrl: async () => {
      // HEIC can't render in browsers — use a large Dropbox-converted JPEG.
      if (isHeicName(file.name) || !file.link) {
        const r = await axios.get(`${API_BASE}/media/thumbnail?path=${encodeURIComponent(file.path)}&size=w2048h1536`,
          { ...authH(), responseType: 'blob' });
        return URL.createObjectURL(r.data);
      }
      return file.link;
    },
  }));
  const loadFiles = () => {
    if (!isReal) return;
    axios.get(`${API_BASE}/bids/${bid.id}/files`, authH()).then(r => setFiles(r.data)).catch(() => setFiles({ images: [], videos: [], documents: [] }));
  };
  useEffect(() => { if (section === 'files') loadFiles(); }, [section]); // eslint-disable-line react-hooks/exhaustive-deps
  // Drag & drop anywhere on the Files tab — each file routes to its bucket by
  // type (images/videos/documents), same rule as the Add Bid photo zone.
  const [filesDragging, setFilesDragging] = useState(false);
  const uploadDropped = async (list) => {
    const dropped = Array.from(list || []).filter(Boolean);
    if (!dropped.length) return;
    const bucketOf = (f) => (f.type.startsWith('image/') || /\.hei[cf]$/i.test(f.name)) ? 'images'
      : (f.type.startsWith('video/') || /\.(mp4|mov|avi|wmv|webm|mkv)$/i.test(f.name)) ? 'videos' : 'documents';
    let failed = 0;
    for (let i = 0; i < dropped.length; i++) {
      setUploading(`${i + 1} of ${dropped.length}`);
      try {
        const stamped = await stampFileName(dropped[i]); // taken-date into the name
        const fd = new FormData(); fd.append('file', stamped); fd.append('targetFolder', bucketOf(dropped[i]));
        await axios.post(`${API_BASE}/bids/${bid.id}/upload`, fd, authH());
      } catch (_) { failed += 1; }
    }
    setUploading('');
    loadFiles();
    if (failed) window.alert(`${failed} of ${dropped.length} files failed to upload — try those again.`);
  };

  const uploadFile = (bucket) => async (e) => {
    const file = e.target.files && e.target.files[0]; e.target.value = '';
    if (!file) return;
    setUploading(bucket);
    const stamped = await stampFileName(file); // taken-date into the name, like project uploads
    const fd = new FormData(); fd.append('file', stamped); fd.append('targetFolder', bucket);
    axios.post(`${API_BASE}/bids/${bid.id}/upload`, fd, authH())
      .then(loadFiles)
      .catch(err => window.alert('Upload failed: ' + (err.response?.data?.error || err.message)))
      .finally(() => setUploading(''));
  };

  // ---- activity log (real bids) ----
  const [logOpen, setLogOpen] = useState(false);
  const [channel, setChannel] = useState('call');
  const [direction, setDirection] = useState('outbound');
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [actMenu, setActMenu] = useState(null);
  const [editingActId, setEditingActId] = useState(null);
  const [editActOutcome, setEditActOutcome] = useState('');
  const [editActNote, setEditActNote] = useState('');
  const submitLog = () => {
    if (!note.trim() && !outcome.trim()) return;
    onLog(bid.id, { channel, direction, note: note.trim(), outcome: outcome.trim() });
    setNote(''); setOutcome('');
  };
  const startEditAct = (act) => { setEditingActId(act._id); setEditActOutcome(act.outcome || ''); setEditActNote(act.note || ''); setActMenu(null); };
  const saveEditAct = () => { onEditActivity(bid.id, editingActId, { outcome: editActOutcome.trim(), note: editActNote.trim() }); setEditingActId(null); };

  // ---- click-to-copy for customer contact + site address ----
  const [copiedField, setCopiedField] = useState('');
  const copyVal = (text, field) => {
    if (!text) return;
    try { navigator.clipboard && navigator.clipboard.writeText(text); } catch (_) {}
    setCopiedField(field); setTimeout(() => setCopiedField(''), 1200);
  };
  const CopyRow = ({ icon: Icon, label, value, field, tint }) => (
    <button type="button" onClick={() => copyVal(value, field)} disabled={!value}
      className="w-full flex items-center justify-between gap-3 py-2 border-b border-gray-800/50 last:border-0 text-left group">
      <span className="flex items-center gap-2 text-xs text-zinc-500 shrink-0"><Icon className={`w-3.5 h-3.5 ${tint || 'text-zinc-500'}`} /> {label}</span>
      <span className="flex items-center gap-2 text-sm text-zinc-200 min-w-0">
        <span className="break-all text-right">{value || <span className="text-zinc-600">Not on file</span>}</span>
        {value && (copiedField === field ? <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <Copy className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-400 shrink-0" />)}
      </span>
    </button>
  );
  const siteAddr = [bid.jobSiteAddress?.street, bid.jobSiteAddress?.city, `${bid.jobSiteAddress?.state || ''} ${bid.jobSiteAddress?.zip || ''}`.trim()].filter(Boolean).join(', ');

  const m = BID_STAGE_META[bid.stage]; const f = followLabel(bid.due);
  const margin = d?.estCost ? Math.round(((bid.value - d.estCost) / bid.value) * 100) : null;
  const profit = d?.estCost ? bid.value - d.estCost : null;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const kindDot = { won: '#34d399', bid: '#fbbf24', lead: '#818cf8', note: '#a1a1aa' };
  const docCount = d?.documents?.length || 0; const photoCount = d?.photos?.length || 0;
  const tabs = isReal
    ? [{ key: 'overview', label: 'Overview', icon: ListChecks }, { key: 'quotes', label: `Quotes & Proposals${(quotes.length + bidProposals.length) ? ` (${quotes.length + bidProposals.length})` : ''}`, icon: FileText }, { key: 'files', label: 'Files', icon: FolderOpen }, { key: 'activity', label: `Activity${bid.activities?.length ? ` (${bid.activities.length})` : ''}`, icon: Clock }]
    : [{ key: 'overview', label: 'Overview', icon: ListChecks }, { key: 'docs', label: `Docs${docCount ? ` (${docCount})` : ''}`, icon: FileText }, { key: 'photos', label: `Photos${photoCount ? ` (${photoCount})` : ''}`, icon: Camera }, { key: 'activity', label: 'Activity', icon: Clock }];
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="drawer-in relative w-[560px] max-w-full h-full bg-gray-950 border-l border-gray-800 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-gray-950/95 backdrop-blur border-b border-gray-800/70 px-6 pt-5">
          {backPerson && onBackToPerson && (
            <button onClick={onBackToPerson} className="mb-3 -mt-1 flex items-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to {backPerson.name}
            </button>
          )}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1.5"><span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${m.chip}`}><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.dot }} /> {bid.stage}</span><SourceTag source={bid.source} /></div>
              <h2 className="text-lg font-bold text-white leading-snug">{bid.title}</h2>
              <p className="text-sm text-zinc-400 mt-0.5">{TYPE_META[bid.type]} {bid.who}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {isReal && !editing && <button onClick={openEdit} title="Edit bid" className="p-2 rounded-lg hover:bg-gray-800 text-zinc-400"><Pencil className="w-4 h-4" /></button>}
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-zinc-400"><X className="w-5 h-5" /></button>
            </div>
          </div>
          {isReal && !bid.project && (
            <button onClick={() => navigate(`/billing/create-quote?bid=${bid.id}`)}
              className="mt-3 w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2">
              <FileText className="w-4 h-4" /> Create Quote — send this bid out
            </button>
          )}
          {isReal && bid.project && (
            <p className="mt-3 text-xs text-emerald-300 font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Promoted to a project — files and billing live there now.</p>
          )}
          <div className="mt-4 grid grid-cols-3 max-md:gap-1.5 gap-2">
            <ValueStat label="Bid value" value={money(bid.value)} tint="text-emerald-300" />
            <ValueStat label="Est. cost" value={d?.estCost != null ? money(d.estCost) : '—'} tint="text-zinc-300" />
            <ValueStat label="Margin" value={margin != null ? `${margin}%` : '—'} tint="text-indigo-300" sub={profit != null ? `${money(profit)} profit` : ''} />
          </div>
          <div className="flex gap-1 mt-4 -mb-px">
            {tabs.map(t => <button key={t.key} onClick={() => setSection(t.key)} className={`px-3 py-2.5 text-xs font-semibold flex items-center gap-1.5 border-b-2 ${section === t.key ? 'border-indigo-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}><t.icon className="w-3.5 h-3.5" /> {t.label}</button>)}
          </div>
        </div>
        <div className="px-6 py-5">
          {/* ---- inline edit form (real bids) ---- */}
          {isReal && editing && form && (
            <div className="mb-5 bg-gray-900/70 border border-indigo-600/40 rounded-xl p-4 space-y-2.5">
              <p className="text-xs font-bold uppercase tracking-wider text-indigo-300">Edit bid</p>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Job title" className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60" />
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="Value ($)" className="px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60" />
                <input type="date" value={form.due} onChange={e => setForm({ ...form, due: e.target.value })} className="px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60" title="Follow-up / due date" />
              </div>
              <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60">
                <option value="">— source —</option>
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} placeholder="Street" className="px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60" />
                <div className="grid grid-cols-[1.4fr_.8fr] gap-2">
                  <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60" />
                  <input value={form.zip} onChange={e => setForm({ ...form, zip: e.target.value })} placeholder="ZIP" className="px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60" />
                </div>
              </div>
              <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes" className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60 resize-none" />
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="flex-1 py-2 bg-gray-800 hover:bg-gray-700 text-zinc-200 rounded-lg text-sm font-semibold">Cancel</button>
                <button onClick={saveEdit} className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-bold">Save</button>
              </div>
            </div>
          )}

          {/* ---- real bid: overview ---- */}
          {isReal && section === 'overview' && (
            <div className="space-y-5">
              {/* customer contact + site — click any row to copy */}
              <div className="bg-gray-900/50 border border-gray-800/60 rounded-xl px-4 py-1">
                <CopyRow icon={Phone} label="Phone" value={bid.customerPhone} field="phone" tint="text-emerald-300" />
                <CopyRow icon={Mail} label="Email" value={bid.customerEmail} field="email" tint="text-violet-300" />
                <CopyRow icon={MapPin} label="Site address" value={siteAddr} field="site" tint="text-sky-300" />
              </div>
              {siteAddr && <JobsiteMiniMap address={siteAddr} />}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
                {f && <Fact icon={CalendarClock} label="Follow-up" value={f.label} tone />}
                {bid.source && <Fact icon={Target} label="Source" value={bid.source} />}
              </div>
              {bid.notes && <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Notes</p><p className="text-sm text-zinc-400 leading-relaxed bg-gray-900/60 border border-gray-800/60 rounded-xl p-3.5 whitespace-pre-wrap">{bid.notes}</p></div>}
            </div>
          )}

          {/* ---- real bid: quotes on this opportunity ---- */}
          {isReal && section === 'quotes' && (
            <div className="space-y-2">
              {/* verbal quote — phone/text price with a short comment; bid-only */}
              {vqOpen ? (
                <div className="bg-gray-900 border border-amber-700/40 rounded-xl p-3.5 space-y-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-300/80">Verbal quote</p>
                  <input autoFocus type="text" inputMode="decimal" placeholder="$0.00"
                    value={fmtMoneyTyping(vqPrice)}
                    onChange={e => setVqPrice(e.target.value.replace(/[^\d.]/g, ''))}
                    className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/60" />
                  <input type="text" placeholder="Comment — e.g. ballpark over the phone, labor only" maxLength={140}
                    value={vqNote} onChange={e => setVqNote(e.target.value)}
                    className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/60" />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setVqOpen(false)} className="px-3 py-1.5 rounded-lg text-xs text-zinc-400 hover:text-zinc-200">Cancel</button>
                    <button onClick={saveVq} disabled={!Number(vqPrice)} className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 disabled:opacity-40">Save</button>
                  </div>
                </div>
              ) : (bid.quotedPrice != null && bid.quotedPrice > 0) ? (
                <div className="flex items-center justify-between gap-3 bg-gray-900 border border-amber-700/40 rounded-xl px-3.5 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-100">
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded mr-1.5 bg-amber-500/15 text-amber-300">Verbal</span>
                      {money(bid.quotedPrice)}
                    </p>
                    <p className="text-[11px] text-zinc-500 truncate">{bid.quotedNote || 'No comment'}{bid.quotedAt ? ` · ${timeAgo(bid.quotedAt)}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={openVq} title="Edit verbal quote" className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-gray-800"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={removeVq} title="Remove verbal quote" className="p-1.5 rounded-lg text-zinc-500 hover:text-red-300 hover:bg-gray-800"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ) : (
                <button onClick={openVq}
                  className="w-full py-2.5 border border-dashed border-amber-700/50 rounded-xl text-xs text-amber-300/70 hover:text-amber-200 hover:border-amber-500/60 flex items-center justify-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> Add verbal quoted price
                </button>
              )}
              {[...quotes.map(q => ({ ...q, _kind: 'quote' })), ...bidProposals.map(q => ({ ...q, _kind: 'proposal', total: q.totalLabor }))]
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map(q => (
                <div key={q._id} onClick={() => navigate(q._kind === 'proposal' ? `/billing/create-proposal?id=${q._id}` : `/billing/create-quote?id=${q._id}&bid=${bid.id}`)}
                  className="w-full flex items-center justify-between gap-3 bg-gray-900 border border-gray-800/60 rounded-xl px-3.5 py-3 hover:bg-gray-800/70 cursor-pointer">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-100">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded mr-1.5 ${q._kind === 'proposal' ? 'bg-rose-500/15 text-rose-300' : 'bg-indigo-500/15 text-indigo-300'}`}>{q._kind === 'proposal' ? 'PS' : 'QT'}</span>
                      {q.number}
                    </p>
                    <p className="text-[11px] text-zinc-500">{timeAgo(q.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    {invoiceOf(q) ? (
                      // Done-state: plain pill (not a button), same green as the
                      // Accepted pill — the work of this row is finished.
                      <span
                        title={invoiceOf(q).number}
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300"
                      >
                        Invoice Created
                      </span>
                    ) : (
                      // Action-state: indigo so it can't be mistaken for an
                      // "already invoiced" green status.
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/billing/create-invoice?${q._kind === 'proposal' ? 'fromProposal' : 'fromQuote'}=${q._id}`);
                        }}
                        title={`Create an invoice prefilled from this ${q._kind === 'proposal' ? 'proposal' : 'quote'}`}
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-600/25 text-indigo-300 hover:bg-indigo-600/40"
                      >
                        + Create Invoice
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (qsMenu?.id === q._id) { setQsMenu(null); return; }
                        const r = e.currentTarget.getBoundingClientRect();
                        const menuH = QUOTE_STATUSES.length * 30 + 12;
                        const up = r.bottom + menuH > window.innerHeight - 8;
                        setQsMenu({ id: q._id, kind: q._kind, x: r.left, y: up ? r.top - menuH - 4 : r.bottom + 4 });
                      }}
                      title="Click to change status"
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full hover:ring-2 hover:ring-white/20 transition-shadow ${QS_TONE[q.status] || 'bg-zinc-700/50 text-zinc-300'}`}>
                      {q.status || 'Draft'}
                    </button>
                    <span className="text-sm font-bold text-emerald-300">{money(q.total || 0)}</span>
                  </div>
                </div>
              ))}
              {qsMenu && (
                <>
                  <span className="fixed inset-0 z-[60]" onClick={(e) => { e.stopPropagation(); setQsMenu(null); }} />
                  <div className="fixed z-[70] bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-1 flex flex-col min-w-[8rem]"
                    style={{ left: Math.min(qsMenu.x, (window.innerWidth || 800) - 140), top: Math.max(8, qsMenu.y) }}>
                    {QUOTE_STATUSES.map(s => (
                      <button key={s} onClick={(e) => { e.stopPropagation(); setPaperStatus(qsMenu.id, s, qsMenu.kind); }} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-gray-800 text-left">
                        <span className={`w-2 h-2 rounded-full ${(QS_TONE[s] || 'bg-zinc-600').split(' ')[0]}`} />
                        <span className="text-[12px] text-zinc-300">{s}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
              {quotes.length === 0 && bidProposals.length === 0 && <p className="text-zinc-600 text-sm py-6 text-center">No quotes or proposals on this bid yet.</p>}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => navigate(`/billing/create-quote?bid=${bid.id}`)}
                  className="py-2.5 border border-dashed border-gray-700 rounded-xl text-xs text-zinc-400 hover:text-zinc-200 hover:border-gray-600 flex items-center justify-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> New quote
                </button>
                <button onClick={() => navigate(`/billing/create-proposal?bid=${bid.id}`)}
                  className="py-2.5 border border-dashed border-rose-700/60 rounded-xl text-xs text-rose-300/80 hover:text-rose-200 hover:border-rose-500/60 flex items-center justify-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> New labor proposal
                </button>
              </div>
            </div>
          )}

          {/* ---- real bid: files (staged in /{year}/Bids until promotion) ---- */}
          {isReal && section === 'files' && (
            <div
              className="relative space-y-5 rounded-2xl"
              onDragOver={(e) => {
                if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setFilesDragging(true); }
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setFilesDragging(false);
              }}
              onDrop={(e) => {
                if (!e.dataTransfer?.files?.length) return;
                e.preventDefault();
                setFilesDragging(false);
                uploadDropped(e.dataTransfer.files);
              }}
            >
              {filesDragging && (
                <div className="absolute -inset-2 z-30 rounded-2xl border-2 border-dashed border-indigo-500 bg-indigo-500/10 pointer-events-none" />
              )}
              {uploading && (
                <p className="text-[11px] font-black uppercase tracking-widest text-indigo-300 animate-pulse">
                  Media Uploading…{uploading.includes(' of ') ? ` ${uploading}` : ''}
                </p>
              )}
              {['images', 'videos', 'documents'].map(bucket => (
                <div key={bucket}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">{bucket}</p>
                    <div className="flex items-center gap-1.5">
                      <label className={`text-[11px] font-bold px-2.5 py-1 rounded-lg cursor-pointer ${uploading === bucket ? 'bg-gray-800 text-zinc-500' : 'bg-indigo-600/20 text-indigo-300 hover:bg-indigo-600/30'}`}>
                        {uploading === bucket ? 'Uploading…' : '+ Upload'}
                        <input type="file" className="hidden" onChange={uploadFile(bucket)} accept={bucket === 'images' ? 'image/*,.heic,.heif' : bucket === 'videos' ? 'video/*' : undefined} disabled={!!uploading} />
                      </label>
                      <button
                        type="button"
                        onClick={() => setFileView(v => v === 'grid' ? 'list' : 'grid')}
                        title={fileView === 'grid' ? 'Switch to line view' : 'Switch to grid view'}
                        className="p-1.5 rounded-lg bg-gray-800/70 text-zinc-400 hover:text-zinc-200 hover:bg-gray-800"
                      >
                        {fileView === 'grid' ? <ListIcon className="w-3.5 h-3.5" /> : <LayoutGrid className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                  {!files && <p className="text-zinc-600 text-xs">Loading…</p>}
                  {files && files[bucket].length === 0 && <p className="text-zinc-600 text-xs">Nothing yet.</p>}
                  {files && files[bucket].length > 0 && (fileView === 'grid' ? (
                    <div className="grid grid-cols-3 max-md:grid-cols-2 gap-2">
                      {files[bucket].map((file, i) => (
                        <a key={i} href={file.link || '#'} target="_blank" rel="noreferrer"
                          onClick={bucket === 'images' ? (e) => { e.preventDefault(); setLightboxIdx(i); } : undefined}
                          onContextMenu={fileContext(file)}
                          className="relative aspect-square rounded-xl overflow-hidden bg-gray-900 border border-gray-800/60 hover:border-gray-600 group">
                          {bucket === 'documents' ? (
                            file.thumbLink ? (
                              <img src={file.thumbLink} alt={file.name} loading="lazy" className="w-full h-full object-cover object-top bg-white group-hover:scale-[1.03] transition-transform" />
                            ) : (
                              <span className="w-full h-full flex items-center justify-center"><FileText className="w-7 h-7 text-indigo-400" /></span>
                            )
                          ) : (
                            <BidThumb
                              path={file.path}
                              alt={file.name}
                              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                              fallback={bucket === 'videos' ? <Camera className="w-7 h-7 text-violet-400" /> : <ImageIcon className="w-7 h-7 text-sky-400" />}
                            />
                          )}
                          <span title={file.name} className="absolute bottom-0 inset-x-0 bg-black/65 px-1.5 py-0.5 text-[9px] text-zinc-300 truncate">{bucket === 'documents' ? file.name : tileCaption(file)}</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    files[bucket].map((file, i) => (
                      <a key={i} href={file.link || '#'} target="_blank" rel="noreferrer"
                        onClick={bucket === 'images' ? (e) => { e.preventDefault(); setLightboxIdx(i); } : undefined}
                        onContextMenu={fileContext(file)}
                        className="flex items-center gap-3 bg-gray-900 border border-gray-800/60 rounded-xl px-3.5 py-2.5 mb-1.5 hover:bg-gray-800/70">
                        <span className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center shrink-0 overflow-hidden">
                          {bucket === 'documents'
                            ? (file.thumbLink ? <img src={file.thumbLink} alt="" loading="lazy" className="w-full h-full object-cover object-top bg-white" /> : <FileText className="w-4 h-4 text-indigo-400" />)
                            : <BidThumb path={file.path} className="w-full h-full object-cover"
                                fallback={bucket === 'videos' ? <Camera className="w-4 h-4 text-violet-400" /> : <ImageIcon className="w-4 h-4 text-sky-400" />} />}
                        </span>
                        <span title={file.name} className="text-sm text-zinc-200 truncate flex-1">{bucket === 'documents' ? file.name : tileCaption(file)}</span>
                        <span className="text-[10px] text-zinc-600 shrink-0">{file.size ? `${Math.round(file.size / 1024)} KB` : ''}</span>
                      </a>
                    ))
                  ))}
                </div>
              ))}
              <p className="text-[11px] text-zinc-600">Files stage in this year’s <span className="text-zinc-400 font-mono">/Bids</span> folder. Winning the bid moves them into the project’s ProjectFiles automatically.</p>
            </div>
          )}
          {lightboxIdx != null && lightboxImages.length > 0 && (
            <ImageLightbox images={lightboxImages} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
          )}
          {fileCtx && (
            <>
              <div className="fixed inset-0 z-[70]" onClick={() => setFileCtx(null)} onContextMenu={(e) => { e.preventDefault(); setFileCtx(null); }} />
              <div className="fixed z-[80] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-1 min-w-[11rem]"
                style={{ left: Math.min(fileCtx.x, (window.innerWidth || 800) - 190), top: Math.min(fileCtx.y, (window.innerHeight || 600) - 60) }}>
                <button onClick={confirmDeleteFile} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-600/15 text-left text-sm text-red-400">
                  <Trash2 className="w-3.5 h-3.5" /> Delete file
                </button>
              </div>
            </>
          )}

          {/* ---- real bid: activity log + timeline ---- */}
          {isReal && section === 'activity' && (
            <div>
              <button onClick={() => setLogOpen(o => !o)} className="w-full flex items-center justify-between group mb-1">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500 group-hover:text-zinc-300 flex items-center gap-1.5"><Plus className={`w-3.5 h-3.5 transition-transform ${logOpen ? 'rotate-45' : ''}`} /> Log activity</span>
                <ChevronDown className={`w-4 h-4 text-zinc-600 transition-transform ${logOpen ? 'rotate-180' : ''}`} />
              </button>
              {logOpen && (
                <div className="mt-2 mb-4">
                  <div className="flex gap-1.5 mb-3">{Object.entries(CHANNELS).map(([key, c]) => { const Icon = CHANNEL_ICON[key]; const on = channel === key; return <button key={key} onClick={() => setChannel(key)} className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold ${on ? `${c.bg} ${c.tone} ring-1 ring-white/10` : 'bg-gray-900 text-zinc-500 hover:text-zinc-300'}`}><Icon className="w-4 h-4" /> {c.label}</button>; })}</div>
                  <div className="flex gap-2 mb-2"><button onClick={() => setDirection('outbound')} className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${direction === 'outbound' ? 'bg-indigo-600 text-white' : 'bg-gray-900 text-zinc-400'}`}><ArrowUpRight className="w-3.5 h-3.5" /> Outbound</button><button onClick={() => setDirection('inbound')} className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${direction === 'inbound' ? 'bg-emerald-600 text-white' : 'bg-gray-900 text-zinc-400'}`}><ArrowDownLeft className="w-3.5 h-3.5" /> Inbound</button></div>
                  <input value={outcome} onChange={e => setOutcome(e.target.value)} placeholder="Outcome (e.g. Quoted, Waiting on customer)" className="w-full mb-2 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60" />
                  <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="What happened?" className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60 resize-none" />
                  <button onClick={submitLog} className="w-full mt-2 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-bold">Log {CHANNELS[channel].label.toLowerCase()}</button>
                </div>
              )}
              <div className="relative pl-2 mt-3"><div className="absolute left-[26px] top-2 bottom-2 w-px bg-gray-800" /><div className="space-y-4">
                {(bid.activities || []).map((act) => { const DirIcon = act.direction === 'inbound' ? ArrowDownLeft : ArrowUpRight; return (
                  <div key={act._id} onContextMenu={(e) => { e.preventDefault(); setActMenu({ act, x: e.clientX, y: e.clientY }); }} className="relative flex gap-3 fade-up"><div className="relative z-10"><ChannelBadge channel={act.channel} /></div>
                    {editingActId === act._id ? (
                      <ActivityEditor outcome={editActOutcome} note={editActNote} setOutcome={setEditActOutcome} setNote={setEditActNote} onSave={saveEditAct} onCancel={() => setEditingActId(null)} />
                    ) : (
                    <div className="flex-1 min-w-0 bg-gray-900/70 border border-gray-800/70 rounded-xl px-3.5 py-2.5">
                      <div className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 text-xs font-semibold text-zinc-300"><DirIcon className={`w-3.5 h-3.5 ${act.direction === 'inbound' ? 'text-emerald-400' : 'text-indigo-400'}`} /><span className="capitalize">{act.channel}</span>{act.outcome && <span className="text-[10px] font-bold bg-zinc-700/50 text-zinc-300 px-1.5 py-0.5 rounded">{act.outcome}</span>}</span><span className="text-[11px] text-zinc-500 shrink-0">{timeAgo(act.at)}</span></div>
                      {act.note && <p className="text-sm text-zinc-400 mt-1 leading-snug">{act.note}</p>}
                    </div>
                    )}
                  </div>
                ); })}
                {(bid.activities || []).length === 0 && <p className="text-zinc-600 text-sm py-6 text-center">No activity yet — log your first touch above.</p>}
              </div></div>
              <ActivityContextMenu menu={actMenu} onClose={() => setActMenu(null)}
                onEdit={() => startEditAct(actMenu.act)}
                onDelete={() => { onDeleteActivity(bid.id, actMenu.act._id); setActMenu(null); }} />
            </div>
          )}

          {(bid.totalValue || bid.referredBy) && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm mb-5">
              {bid.totalValue ? <Fact icon={DollarSign} label="Total project (GC scope)" value={money(bid.totalValue)} /> : null}
              {bid.referredBy ? <Fact icon={UsersIcon} label="Referred by" value={bid.referredBy} /> : null}
            </div>
          )}
          {!isReal && !d && <p className="text-zinc-600 text-sm text-center py-12">No estimate detail captured for this job yet.</p>}
          {d && section === 'overview' && (
            <div className="space-y-5">
              {d.status && <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-zinc-500" /><span className="text-zinc-300 font-medium">{d.status}</span></div>}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
                {d.address && <Fact icon={MapPin} label="Site" value={d.address} full />}
                {bid.contactId && <Fact icon={Link2} label="Relationship" value={bid.who} />}
                {f && <Fact icon={CalendarClock} label="Bid due" value={f.label} tone />}
                {d.awardedDate && <Fact icon={Trophy} label="Awarded" value={fmtDate(d.awardedDate)} />}
                {d.startDate && <Fact icon={CalendarClock} label="Start" value={fmtDate(d.startDate)} />}
                {d.deposit != null && <Fact icon={DollarSign} label="Deposit" value={money(d.deposit)} />}
                {d.competitors && <Fact icon={UsersIcon} label="Competition" value={d.competitors} />}
                {d.nextStep && <Fact icon={ArrowUpRight} label="Next step" value={d.nextStep} full />}
              </div>
              {d.scope && <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Scope of work</p><p className="text-sm text-zinc-400 leading-relaxed bg-gray-900/60 border border-gray-800/60 rounded-xl p-3.5">{d.scope}</p></div>}
              {d.lineItems?.length > 0 && (
                <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Estimate</p>
                  <div className="rounded-xl border border-gray-800/60 overflow-hidden">
                    {d.lineItems.map((li, i) => <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm border-b border-gray-800/40 last:border-0"><span className="text-zinc-300">{li.label}</span><span className="text-zinc-200 font-medium">{money(li.amount)}</span></div>)}
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-900/70 text-sm font-bold"><span className="text-zinc-200">Total</span><span className="text-emerald-300">{money(bid.value)}</span></div>
                  </div>
                </div>
              )}
            </div>
          )}
          {d && section === 'docs' && (
            <div className="space-y-2">{d.documents?.map((doc, i) => <div key={i} className="flex items-center gap-3 bg-gray-900 border border-gray-800/60 rounded-xl px-3.5 py-3"><span className="w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-indigo-400" /></span><div className="flex-1 min-w-0"><p className="text-sm text-zinc-200 truncate">{doc.name}</p></div>{doc.type && <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${DOC_TONE[doc.type] || 'bg-zinc-700/40 text-zinc-300'}`}>{doc.type}</span>}</div>)}</div>
          )}
          {d && section === 'photos' && (
            <div className="grid grid-cols-2 gap-2.5">{d.photos?.map((ph, i) => <div key={i} className={`relative aspect-[4/3] rounded-xl overflow-hidden bg-gradient-to-br ${ph.tone} ring-1 ring-white/5`}><div className="absolute inset-0 flex items-center justify-center opacity-30"><ImageIcon className="w-7 h-7 text-white" /></div><div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2.5 py-1.5"><p className="text-[11px] font-medium text-white truncate">{ph.caption}</p></div></div>)}</div>
          )}
          {d && section === 'activity' && (
            <div className="relative pl-1 space-y-3">{d.timeline?.map((t, i) => <div key={i} className="flex items-start gap-3"><span className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: kindDot[t.kind] || '#a1a1aa' }} /><div className="flex-1 min-w-0"><p className="text-sm text-zinc-300 leading-snug">{t.label}</p><p className="text-[11px] text-zinc-600">{timeAgo(t.at)}</p></div></div>)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
function ValueStat({ label, value, tint, sub }) {
  return <div className="rounded-xl bg-gray-900/70 border border-gray-800/60 px-3 py-2.5"><p className={`text-lg font-bold ${tint}`}>{value}</p><p className="text-[10px] text-zinc-500 font-medium">{label}</p>{sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}</div>;
}
function Fact({ icon: Icon, label, value, full, tone }) {
  return <div className={`flex items-start gap-2 ${full ? 'col-span-2' : ''}`}><Icon className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" /><div className="min-w-0"><p className="text-[10px] text-zinc-500 uppercase tracking-wide font-semibold">{label}</p><p className={`text-sm ${tone ? 'text-amber-300 font-semibold' : 'text-zinc-300'} break-words`}>{value}</p></div></div>;
}

// Right-click menu + inline editor shared by the outreach/conversation timelines.
function ActivityContextMenu({ menu, onEdit, onDelete, onClose }) {
  if (!menu) return null;
  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div className="fixed z-[70] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-1 min-w-[9rem]"
        style={{ top: Math.min(menu.y, (window.innerHeight || 800) - 110), left: Math.min(menu.x, (window.innerWidth || 800) - 160) }}>
        <button onClick={onEdit} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-800 text-left text-sm text-zinc-200"><Pencil className="w-3.5 h-3.5 text-zinc-400" /> Edit</button>
        <button onClick={onDelete} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-600/15 text-left text-sm text-red-400"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
      </div>
    </>
  );
}
// Address block for edit forms — Google Places search by default, chip once
// filled, manual fields as the escape hatch. Works on a {street,city,state,zip}
// slice of the parent form. Mirrors the Customers-page pattern.
function AddressFields({ form, setForm }) {
  const fullOf = (f) => [f.street, f.city, `${f.state || ''} ${f.zip || ''}`.trim()].filter(Boolean).join(', ');
  const [q, setQ] = useState(() => fullOf(form)); // the field holds the current address, like any other field
  const [sugs, setSugs] = useState([]);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState(false);
  const timer = useRef(null);

  const onSearch = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (val.trim().length < 3) { setSugs([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      const { predictions, error } = await getAddressSuggestions(val);
      setErr(error); setSugs(predictions); setOpen(true);
    }, 350);
  };
  const pick = async (p) => {
    setOpen(false);
    try {
      const parts = await geocodePlaceId(p.placeId);
      setForm(prev => ({ ...prev, street: parts.street, city: parts.city, state: parts.state, zip: parts.zip }));
      setQ(fullOf(parts));
    } catch (_) {
      setForm(prev => ({ ...prev, street: p.description }));
      setQ(p.description);
    }
  };
  const clearAddr = () => {
    setQ(''); setSugs([]); setOpen(false);
    setForm(prev => ({ ...prev, street: '', city: '', state: 'CA', zip: '' }));
  };
  const inputCls = "w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60";
  const linkCls = "text-[12px] text-zinc-500 hover:text-zinc-300";
  const lblCls = "block text-[11px] uppercase tracking-wider text-zinc-500 mb-1";

  if (manual) return (
    <div className="space-y-3">
      <div><label className={lblCls}>Street</label><input value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} className={inputCls} /></div>
      <div className="grid grid-cols-3 gap-2">
        <div><label className={lblCls}>City</label><input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className={inputCls} /></div>
        <div><label className={lblCls}>State</label><input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className={inputCls} /></div>
        <div><label className={lblCls}>ZIP</label><input value={form.zip} onChange={e => setForm({ ...form, zip: e.target.value })} className={inputCls} /></div>
      </div>
      <button type="button" onClick={() => setManual(false)} className={linkCls}>← use address search instead</button>
    </div>
  );

  return (
    <div className="relative">
      <label className={lblCls}>Address</label>
      <div className="relative">
        <input value={q} onChange={e => onSearch(e.target.value)}
          onFocus={() => { if (sugs.length || err) setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 250)}
          placeholder="Start typing street, city or ZIP…" className={`${inputCls} pr-8`} />
        {q && (
          <button type="button" onMouseDown={(e) => { e.preventDefault(); clearAddr(); }} title="Clear address"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-gray-800">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-gray-900 border border-gray-700 rounded-xl max-h-48 overflow-auto z-[70] shadow-xl">
          {err ? <div className="px-3 py-2.5 text-red-400 text-xs">{err}</div>
            : sugs.length === 0 ? <div className="px-3 py-2.5 text-zinc-500 text-xs">No matching addresses</div>
            : sugs.map((p, i) => (
              <div key={i} onClick={() => pick(p)} className="px-3 py-2.5 hover:bg-gray-800 cursor-pointer text-sm text-zinc-200 border-b border-gray-800/50 last:border-0">{p.description}</div>
            ))}
          <div onMouseDown={(e) => { e.preventDefault(); setOpen(false); setManual(true); }}
            className="px-3 py-2.5 cursor-pointer text-indigo-400 font-semibold text-xs sticky bottom-0 bg-gray-900 border-t border-gray-800">Enter address manually</div>
        </div>
      )}
      <button type="button" onClick={() => setManual(true)} className={`${linkCls} mt-1.5`}>edit manually</button>
    </div>
  );
}

function ActivityEditor({ outcome, note, setOutcome, setNote, onSave, onCancel, ev = null, setEv = null }) {
  return (
    <div className="flex-1 min-w-0 bg-gray-900/70 border border-indigo-600/40 rounded-xl px-3.5 py-2.5">
      <input value={outcome} onChange={e => setOutcome(e.target.value)} placeholder="Outcome" className="w-full mb-2 px-2.5 py-1.5 bg-gray-950 border border-gray-800 rounded-lg text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60" />
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="What happened?" className="w-full px-2.5 py-1.5 bg-gray-950 border border-gray-800 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60 resize-none" />
      {ev && setEv && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-zinc-600 mb-0.5">Notified date</p>
              <input type="date" value={ev.notifiedDate} onChange={e => setEv(x => ({ ...x, notifiedDate: e.target.value }))} className="w-full px-2.5 py-1 bg-gray-950 border border-gray-800 rounded-lg text-xs text-zinc-200 focus:outline-none focus:border-indigo-600/60" />
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-zinc-600 mb-0.5">Event date</p>
              <input type="date" value={ev.eventDate} onChange={e => setEv(x => ({ ...x, eventDate: e.target.value }))} className="w-full px-2.5 py-1 bg-gray-950 border border-gray-800 rounded-lg text-xs text-zinc-200 focus:outline-none focus:border-indigo-600/60" />
            </div>
          </div>
          <EventTypePicker value={ev.eventType} onChange={(v) => setEv(x => ({ ...x, eventType: v }))} />
        </div>
      )}
      <div className="flex gap-2 mt-2">
        <button onClick={onSave} className="flex-1 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold">Save</button>
        <button onClick={onCancel} className="flex-1 py-1.5 bg-gray-800 hover:bg-gray-700 text-zinc-300 rounded-lg text-xs font-semibold">Cancel</button>
      </div>
    </div>
  );
}

// ======================= COMPANY DRAWER =======================
function ContactDrawer({ contact, people, onClose, onLog, onSetStage, onToggleStar, onOpenPerson, onUpdate, bids, onEditActivity, onDeleteActivity, onAddPerson }) {
  const [channel, setChannel] = useState('call');
  const [direction, setDirection] = useState('outbound');
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [logOpen, setLogOpen] = useState(false); // log form starts collapsed
  const [fuOpen, setFuOpen] = useState(false);   // follow-up popover
  const [followPick, setFollowPick] = useState('keep'); // 'keep' | 'none' | days
  const [actMenu, setActMenu] = useState(null);       // { act, x, y } — right-click menu
  const [editingActId, setEditingActId] = useState(null);
  const [editOutcome, setEditOutcome] = useState('');
  const [editNote, setEditNote] = useState('');
  const toYMD = (d) => {
    if (!d) return '';
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };
  const [editEv, setEditEv] = useState(null); // { notifiedDate, eventDate, eventType } — note entries
  const startEditAct = (act) => {
    setEditingActId(act.id); setEditOutcome(act.outcome || ''); setEditNote(act.note || '');
    setEditEv(act.channel === 'note' ? { notifiedDate: toYMD(act.notifiedDate), eventDate: toYMD(act.eventDate), eventType: act.eventType || '' } : null);
    setActMenu(null);
  };
  const saveEditAct = () => {
    onEditActivity(contact.id, editingActId, {
      outcome: editOutcome.trim(), note: editNote.trim(),
      ...(editEv ? {
        notifiedDate: editEv.notifiedDate ? new Date(`${editEv.notifiedDate}T12:00:00`).toISOString() : null,
        eventDate: editEv.eventDate ? new Date(`${editEv.eventDate}T12:00:00`).toISOString() : null,
        eventType: editEv.eventType || '',
      } : {}),
    });
    setEditingActId(null);
  };
  const companyPeople = people.filter(p =>
    p.companyId === contact.id ||
    (p.company && contact.company && p.company.trim().toLowerCase() === contact.company.trim().toLowerCase())
  );
  // Note-channel event logging: when they notified us, when it happens, what kind.
  const [evForm, setEvForm] = useState({ notifiedDate: '', eventDate: '', eventType: '' });
  const pursuits = (bids || []).filter(b => b.contactId === contact.id);
  const submit = () => {
    if (!note.trim() && !outcome.trim() && !evForm.eventDate) return;
    onLog(contact.id, {
      channel, direction, at: new Date().toISOString(), note: note.trim(), outcome: outcome.trim(),
      ...(channel === 'note' ? {
        notifiedDate: evForm.notifiedDate ? new Date(`${evForm.notifiedDate}T12:00:00`).toISOString() : null,
        eventDate: evForm.eventDate ? new Date(`${evForm.eventDate}T12:00:00`).toISOString() : null,
        eventType: evForm.eventType || '',
      } : {}),
    });
    setEvForm({ notifiedDate: '', eventDate: '', eventType: '' });
    // Reschedule (or clear) the next follow-up along with the logged touch.
    if (followPick === 'none') onUpdate(contact.id, { nextFollowUp: null });
    else if (followPick !== 'keep') onUpdate(contact.id, { nextFollowUp: followUpInDays(followPick) });
    setNote(''); setOutcome(''); setFollowPick('keep');
  };
  const f = followLabel(contact.nextFollowUp);
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="drawer-in relative w-[480px] max-w-full h-full bg-gray-950 border-l border-gray-800 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-gray-950/95 backdrop-blur border-b border-gray-800/70 px-6 py-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <CompanyLogo contact={contact} onUpdate={onUpdate} editable sizeClass="w-14 h-14" roundedClass="rounded-2xl" />
              <div className="min-w-0"><h2 className="text-lg font-bold text-white truncate">{contact.company}</h2>{contact.fullName && <p className="text-xs text-zinc-500 truncate italic">{contact.fullName}</p>}<p className="text-sm text-zinc-400 truncate">{contact.contactName} · {contact.title}</p></div>
            </div>
            <div className="flex items-center gap-1 shrink-0"><button onClick={() => onToggleStar(contact.id)} className="p-2 rounded-lg hover:bg-gray-800"><Star className={`w-4 h-4 ${contact.starred ? 'text-amber-400 fill-amber-400' : 'text-zinc-500'}`} /></button><button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-zinc-400"><X className="w-5 h-5" /></button></div>
          </div>
          {contact.source && <div className="mt-3 flex items-center gap-2 text-xs"><SourceTag source={contact.source} /></div>}
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            {REL_STAGES.map(s => { const sm = REL_STAGE_META[s]; const on = s === contact.relStage; return <StageTip key={s} stage={s}><button onClick={() => onSetStage(contact.id, s)} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ring-1 ${on ? `${sm.chip} ring-white/10 scale-105` : 'bg-gray-900 text-zinc-500 ring-gray-800 hover:text-zinc-300'}`}>{s}</button></StageTip>; })}
          </div>
        </div>
        <div className="px-6 py-4 grid grid-cols-2 gap-2">
          <a href={`tel:${contact.phone}`} className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200"><Phone className="w-4 h-4 text-emerald-300" /><span className="truncate">{fmtPhone(contact.phone)}</span></a>
          <a href={`sms:${contact.phone}`} className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200"><MessageSquare className="w-4 h-4 text-sky-300" /> Text</a>
          <a href={`mailto:${contact.email}`} className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 col-span-2"><Mail className="w-4 h-4 text-violet-300" /><span className="truncate">{contact.email}</span></a>
          {contact.secondaryPhone && (
            <a href={`tel:${contact.secondaryPhone}`} className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 col-span-2"><Phone className="w-4 h-4 text-emerald-300/60" /><span className="truncate">{fmtPhone(contact.secondaryPhone)}</span><span className="text-[10px] text-zinc-600 ml-auto shrink-0">2nd</span></a>
          )}
          {(contact.secondaryEmail || contact.altEmail) && (
            <a href={`mailto:${contact.secondaryEmail || contact.altEmail}`} className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 col-span-2"><Mail className="w-4 h-4 text-violet-300/60" /><span className="truncate">{contact.secondaryEmail || contact.altEmail}</span><span className="text-[10px] text-zinc-600 ml-auto shrink-0">2nd</span></a>
          )}
        </div>
        <div className="px-6 pb-4 space-y-2.5 text-sm">
          <Meta icon={Building2} label={contact.type} value={[contact.licenseClass, contact.licenseNumber ? `CSLB #${contact.licenseNumber}` : ''].filter(Boolean).join(' · ') || (contact.units ? `${contact.units} units` : '')} />
          {contact.address && <Meta icon={MapPin} label={contact.address} />}
          {contact.website && <Meta icon={Globe} label={contact.website} />}
          {/* Next follow-up — always shown, click to reschedule or clear */}
          <div className="flex items-center gap-2 text-zinc-400 relative">
            <CalendarClock className="w-4 h-4 text-zinc-500" /><span>Next follow-up</span>
            <button onClick={() => setFuOpen(o => !o)} title="Click to change"
              className={`text-[11px] font-bold px-2 py-0.5 rounded ring-1 hover:ring-white/30 ${f ? f.tone : 'bg-gray-900 text-zinc-500 ring-gray-800'}`}>
              {f ? f.label : 'None'}
            </button>
            {fuOpen && <FollowUpMenu onPick={(iso) => onUpdate(contact.id, { nextFollowUp: iso })} onClose={() => setFuOpen(false)} />}
          </div>
          {contact.tags?.length > 0 && <div className="flex flex-wrap gap-1.5 pt-1">{contact.tags.map((t, i) => <span key={i} className="text-[11px] font-medium bg-indigo-500/10 text-indigo-300 px-2 py-0.5 rounded-md">{t}</span>)}</div>}
        </div>

        {/* Active pursuits — this company's open bids/jobs, each with its own stage */}
        {pursuits.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-800/60">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Active pursuits</p>
            <div className="space-y-2">
              {pursuits.map(b => { const m = BID_STAGE_META[b.stage] || {}; return (
                <div key={b.id} className="flex items-center justify-between gap-2 bg-gray-900 border border-gray-800/60 rounded-xl px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm text-zinc-200 truncate">{b.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-zinc-400"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.dot || '#a1a1aa' }} />{b.stage}</span>
                      {b.source && <SourceTag source={b.source} />}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-emerald-300 shrink-0">{moneyK(b.value)}</span>
                </div>
              ); })}
            </div>
          </div>
        )}

        {/* People at this company */}
        <div className="px-6 py-4 border-t border-gray-800/60">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">People here</p>
          {companyPeople.length === 0 && <p className="text-zinc-600 text-sm">No people added yet.</p>}
          <div className="space-y-2">
            {companyPeople.map(p => (
              <button key={p.id} onClick={() => onOpenPerson(p.id)} className="w-full flex items-center justify-between gap-2 bg-gray-900 hover:bg-gray-800/70 border border-gray-800/60 rounded-xl px-3 py-2.5 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${avatarGrad(p.id)} flex items-center justify-center text-[11px] font-bold text-white`}>{initials(p.name)}</span>
                  <div className="min-w-0 text-left">
                    <p className="text-sm text-zinc-200 truncate">{p.name}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{p.role}</p>
                    {(p.projectIds || []).length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {p.projectIds.slice(0, 2).map(pid => <span key={pid} className="text-[10px] font-medium bg-sky-500/10 text-sky-300 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><FolderOpen className="w-2.5 h-2.5" />{projectName(pid)}</span>)}
                        {p.projectIds.length > 2 && <span className="text-[10px] text-zinc-500">+{p.projectIds.length - 2} more</span>}
                      </div>
                    )}
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-zinc-600 shrink-0 self-center" />
              </button>
            ))}
            <button onClick={() => onAddPerson && onAddPerson(contact.company)} className="w-full py-2 border border-dashed border-gray-700 rounded-xl text-xs text-zinc-500 hover:text-zinc-300 hover:border-gray-600 flex items-center justify-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add person</button>
          </div>
        </div>

        {/* company outreach log + timeline — form starts collapsed to keep the drawer calm */}
        <div className="px-6 py-4 border-t border-gray-800/60">
          <button onClick={() => setLogOpen(o => !o)} className="w-full flex items-center justify-between group">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-500 group-hover:text-zinc-300 flex items-center gap-1.5"><Plus className={`w-3.5 h-3.5 transition-transform ${logOpen ? 'rotate-45' : ''}`} /> Log outreach</span>
            <ChevronDown className={`w-4 h-4 text-zinc-600 transition-transform ${logOpen ? 'rotate-180' : ''}`} />
          </button>
          {logOpen && (
            <div className="mt-3">
              <div className="flex gap-1.5 mb-3">{Object.entries(CHANNELS).map(([key, c]) => { const Icon = CHANNEL_ICON[key]; const on = channel === key; return <button key={key} onClick={() => setChannel(key)} className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold ${on ? `${c.bg} ${c.tone} ring-1 ring-white/10` : 'bg-gray-900 text-zinc-500 hover:text-zinc-300'}`}><Icon className="w-4 h-4" /> {c.label}</button>; })}</div>
              <div className="flex gap-2 mb-2"><button onClick={() => setDirection('outbound')} className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${direction === 'outbound' ? 'bg-indigo-600 text-white' : 'bg-gray-900 text-zinc-400'}`}><ArrowUpRight className="w-3.5 h-3.5" /> Outbound</button><button onClick={() => setDirection('inbound')} className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${direction === 'inbound' ? 'bg-emerald-600 text-white' : 'bg-gray-900 text-zinc-400'}`}><ArrowDownLeft className="w-3.5 h-3.5" /> Inbound</button></div>
              <input value={outcome} onChange={e => setOutcome(e.target.value)} placeholder="Outcome (e.g. Approved, Sent docs)" className="w-full mb-2 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60" />
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="What happened?" className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60 resize-none" />
              {/* NOTE channel = event logging: when they told us, when it
                  happens, and what kind (same badges as Employee Scheduling). */}
              {channel === 'note' && (
                <div className="mt-2 p-3 bg-gray-950/60 border border-gray-800/70 rounded-xl space-y-2">
                  <div className="grid grid-cols-2 max-md:grid-cols-1 gap-2">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Notified date</p>
                      <input type="date" value={evForm.notifiedDate} onChange={e => setEvForm(f => ({ ...f, notifiedDate: e.target.value }))}
                        className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Event date</p>
                      <input type="date" value={evForm.eventDate} onChange={e => setEvForm(f => ({ ...f, eventDate: e.target.value }))}
                        className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60" />
                    </div>
                  </div>
                  <EventTypePicker value={evForm.eventType} onChange={(v) => setEvForm(f => ({ ...f, eventType: v }))} />
                </div>
              )}
              {/* reschedule the next follow-up with this touch */}
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mr-1">Follow up</span>
                {[{ v: 'keep', l: 'Keep' }, { v: 'none', l: 'None' }, { v: 3, l: '3d' }, { v: 7, l: '1w' }, { v: 14, l: '2w' }, { v: 30, l: '1m' }].map(o => (
                  <button key={o.v} onClick={() => setFollowPick(o.v)}
                    className={`text-[11px] font-semibold px-2 py-1 rounded-lg ${followPick === o.v ? 'bg-indigo-600 text-white' : 'bg-gray-900 text-zinc-500 hover:text-zinc-300'}`}>{o.l}</button>
                ))}
              </div>
              <button onClick={submit} className="w-full mt-2 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-bold">Log {CHANNELS[channel].label.toLowerCase()}</button>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-800/60">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Outreach timeline</p>
          <div className="relative pl-2"><div className="absolute left-[26px] top-2 bottom-2 w-px bg-gray-800" /><div className="space-y-4">
            {contact.activities.map((act) => { const DirIcon = act.direction === 'inbound' ? ArrowDownLeft : ArrowUpRight; return (
              <div key={act.id} onContextMenu={(e) => { e.preventDefault(); setActMenu({ act, x: e.clientX, y: e.clientY }); }} className="relative flex gap-3 fade-up"><div className="relative z-10 cursor-pointer" title="Click to edit this entry" onClick={() => startEditAct(act)}><ChannelBadge channel={act.channel} /></div>
                {editingActId === act.id ? (
                  <ActivityEditor outcome={editOutcome} note={editNote} setOutcome={setEditOutcome} setNote={setEditNote} onSave={saveEditAct} onCancel={() => setEditingActId(null)} ev={editEv} setEv={setEditEv} />
                ) : (
                <div className="flex-1 min-w-0 bg-gray-900/70 border border-gray-800/70 rounded-xl px-3.5 py-2.5">
                  <div className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 text-xs font-semibold text-zinc-300"><DirIcon className={`w-3.5 h-3.5 ${act.direction === 'inbound' ? 'text-emerald-400' : 'text-indigo-400'}`} /><span className="capitalize">{act.channel}</span>{act.outcome && <span className="text-[10px] font-bold bg-zinc-700/50 text-zinc-300 px-1.5 py-0.5 rounded">{act.outcome}</span>}</span><span className="text-[11px] text-zinc-500 shrink-0">{timeAgo(act.at)}</span></div>
                  {act.note && <p className="text-sm text-zinc-400 mt-1 leading-snug">{act.note}</p>}
                  {(act.eventType || act.eventDate || act.notifiedDate) && (
                    <div className="flex items-center gap-2 flex-wrap mt-1.5">
                      <EventBadge type={act.eventType} />
                      {act.notifiedDate && <span className="text-[10px] text-zinc-500">Notified: <span className="text-zinc-300 font-semibold">{new Date(act.notifiedDate).toLocaleDateString()}</span></span>}
                      {act.eventDate && <span className="text-[10px] text-zinc-500">Event: <span className="text-zinc-300 font-semibold">{new Date(act.eventDate).toLocaleDateString()}</span></span>}
                    </div>
                  )}
                </div>
                )}
              </div>
            ); })}
          </div></div>
        </div>
      </div>
      <ActivityContextMenu menu={actMenu} onClose={() => setActMenu(null)}
        onEdit={() => startEditAct(actMenu.act)}
        onDelete={() => { onDeleteActivity(contact.id, actMenu.act.id); setActMenu(null); }} />
    </div>
  );
}
function Meta({ icon: Icon, label, value }) {
  if (!label) return null;
  return <div className="flex items-center gap-2 text-zinc-400"><Icon className="w-4 h-4 text-zinc-500 shrink-0" /><span className="truncate">{label}</span>{value && <span className="text-zinc-600">· {value}</span>}</div>;
}

// ======================= PERSON DRAWER =======================
function PersonDrawer({ person, contacts, onClose, onLog, onUpdate, onOpenCompany, onOpenBid, onEditActivity, onDeleteActivity }) {
  const navigate = useNavigate();
  const [channel, setChannel] = useState('call');
  const [direction, setDirection] = useState('outbound');
  const [proj, setProj] = useState((person.projectIds && person.projectIds[0]) || '');
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [logOpen, setLogOpen] = useState(false); // log form starts collapsed
  const [actMenu, setActMenu] = useState(null);       // { act, x, y } — right-click menu
  const [editingActId, setEditingActId] = useState(null);
  const [editActOutcome, setEditActOutcome] = useState('');
  const [editActNote, setEditActNote] = useState('');
  const startEditAct = (act) => { setEditingActId(act.id); setEditActOutcome(act.outcome || ''); setEditActNote(act.note || ''); setActMenu(null); };
  const saveEditAct = () => { onEditActivity(person.id, editingActId, { outcome: editActOutcome.trim(), note: editActNote.trim() }); setEditingActId(null); };
  const [copied, setCopied] = useState('');
  const copy = (text, id) => { if (!text) return; try { navigator.clipboard && navigator.clipboard.writeText(text); } catch (e) {} setCopied(id); setTimeout(() => setCopied(''), 1200); };
  const copyRow = (Icon, label, value, id, tint) => (
    <button type="button" key={id} onClick={() => copy(value, id)} className="w-full flex items-center justify-between gap-3 py-2 border-b border-gray-800/50 last:border-0 text-left group">
      <span className="flex items-center gap-2 text-xs text-zinc-500 shrink-0"><Icon className={`w-3.5 h-3.5 ${tint || 'text-zinc-500'}`} /> {label}</span>
      <span className="flex items-center gap-2 text-sm text-zinc-200 min-w-0">
        <span className="truncate">{value || <span className="text-zinc-600">Not on file</span>}</span>
        {value && (copied === id ? <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <Copy className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-400 shrink-0" />)}
      </span>
    </button>
  );
  const company = person.companyId ? contacts.find(c => c.id === person.companyId) : null;
  const companyLabel = company ? company.company : (person.companyName || 'Independent');
  const addr = person.address || {};
  const addrLine2 = [[addr.city, addr.state].filter(Boolean).join(', '), addr.zip].filter(Boolean).join(' ');
  const addrFull = [addr.street, addrLine2].filter(Boolean).join(', ') || person.city || '';
  const projCount = (person.projectIds || []).length;
  const lastAt = (person.activities && person.activities[0] && person.activities[0].at) || null;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');

  // Real customers: pull their actual work — projects, open bids, quotes —
  // instead of the prototype's fake project chips.
  const isRealPerson = isMongoId(person.id);
  const [work, setWork] = useState(null); // { projects, bids, quotes } | null
  useEffect(() => {
    if (!isRealPerson) return;
    const H = authH();
    Promise.all([
      axios.get(`${API_BASE}/projects`, H).then(r => (r.data || []).filter(p => (p.customer?._id || p.customer) === person.id)).catch(() => []),
      axios.get(`${API_BASE}/bids`, H).then(r => (r.data || []).filter(b => (b.customer?._id || b.customer) === person.id && !b.project)).catch(() => []),
      axios.get(`${API_BASE}/quotes`, H).then(r => (r.data || []).filter(q => (q.customer?._id || q.customer) === person.id)).catch(() => []),
    ]).then(([projects, bids, quotes]) => setWork({ projects, bids, quotes }));
  }, [person.id, isRealPerson]);
  const openEdit = () => {
    const a = person.address || {};
    setForm({
      name: person.name || '', role: person.role || '', companyName: person.companyName || '',
      phone: person.phone || '', secondaryPhone: person.secondaryPhone || '',
      email: person.email || '', altEmail: person.altEmail || '',
      street: a.street || '', city: a.city || person.city || '', state: a.state || '', zip: a.zip || '',
      tags: (person.tags || []).join(', '), notes: person.notes || '',
      lifetime: person.lifetime != null ? String(person.lifetime) : '', since: person.since || '',
    });
    setEditing(true);
  };
  const saveEdit = () => {
    onUpdate && onUpdate(person.id, {
      name: form.name.trim(), role: form.role.trim(), companyName: form.companyName.trim(),
      phone: form.phone.trim(), secondaryPhone: form.secondaryPhone.trim(),
      email: form.email.trim(), altEmail: form.altEmail.trim(),
      address: { street: form.street.trim(), city: form.city.trim(), state: form.state.trim(), zip: form.zip.trim() },
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      notes: form.notes.trim(),
      lifetime: form.lifetime === '' ? undefined : (Number(form.lifetime) || 0),
      since: form.since.trim(),
    });
    setEditing(false);
  };
  const field = (label, key) => (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      <input value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60" />
    </div>
  );
  const submit = () => { if (!note.trim() && !outcome.trim()) return; onLog(person.id, { channel, direction, at: new Date().toISOString(), note: note.trim(), outcome: outcome.trim(), projectId: proj || null }); setNote(''); setOutcome(''); };
  return (
    <div className="fixed inset-0 z-[55] flex justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="drawer-in relative w-[480px] max-w-full h-full bg-gray-950 border-l border-gray-800 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-gray-950/95 backdrop-blur border-b border-gray-800/70 px-6 py-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="min-w-0"><h2 className="text-lg font-bold text-white truncate">{person.name}{person.nickname ? <span className="text-zinc-400 font-normal"> “{person.nickname}”</span> : null}</h2><p className="text-sm text-zinc-400 truncate">{person.role}</p></div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={openEdit} title="Edit contact" className="p-2 rounded-lg hover:bg-gray-800 text-zinc-400"><Pencil className="w-4 h-4" /></button>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-zinc-400"><X className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs">
            {company ? (
              <button onClick={() => onOpenCompany && onOpenCompany(person.companyId)} title="Open company" className="inline-flex items-center gap-1.5 text-zinc-300 font-semibold hover:text-white group">
                <Building2 className="w-3.5 h-3.5 text-zinc-500" /> {companyLabel}
                <ArrowUpRight className="w-3 h-3 text-zinc-600 group-hover:text-indigo-400" />
              </button>
            ) : person.companyId ? <span className="inline-flex items-center gap-1.5 text-zinc-300 font-semibold"><Building2 className="w-3.5 h-3.5 text-zinc-500" /> {companyLabel}</span>
              : <span className="text-[11px] font-bold px-2 py-1 rounded bg-amber-500/15 text-amber-300 inline-flex items-center gap-1"><User className="w-3 h-3" /> {companyLabel}</span>}
          </div>
        </div>

        <div className="px-6 py-4 grid grid-cols-2 gap-2">
          {/* plain text, click = copy (no tel:/mailto:) */}
          <button type="button" onClick={() => copy(person.phone, 'qa-phone')} className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 text-left">
            <Phone className="w-4 h-4 text-emerald-300 shrink-0" /><span className="truncate flex-1">{fmtPhone(person.phone) || '—'}</span>
            {copied === 'qa-phone' ? <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <Copy className="w-3.5 h-3.5 text-zinc-600 shrink-0" />}
          </button>
          <button type="button" onClick={() => copy(person.email, 'qa-email')} className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 text-left min-w-0">
            <Mail className="w-4 h-4 text-violet-300 shrink-0" /><span className="truncate flex-1">{person.email || '—'}</span>
            {copied === 'qa-email' ? <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <Copy className="w-3.5 h-3.5 text-zinc-600 shrink-0" />}
          </button>
        </div>

        {/* Stats (merged from the customer record) */}
        <div className="px-6 pb-1 grid grid-cols-4 gap-2">
          <div className="bg-gray-900 rounded-xl px-2.5 py-2 text-center"><p className="text-[9px] uppercase tracking-wider text-zinc-500">Projects</p><p className="text-base font-bold text-white mt-0.5">{projCount}</p></div>
          <div className="bg-gray-900 rounded-xl px-2.5 py-2 text-center"><p className="text-[9px] uppercase tracking-wider text-zinc-500">Lifetime</p><p className="text-base font-bold text-white mt-0.5">{person.lifetime ? moneyK(person.lifetime) : '—'}</p></div>
          <div className="bg-gray-900 rounded-xl px-2.5 py-2 text-center"><p className="text-[9px] uppercase tracking-wider text-zinc-500">Last</p><p className="text-base font-bold text-white mt-0.5">{lastAt ? timeAgo(lastAt) : '—'}</p></div>
          <div className="bg-gray-900 rounded-xl px-2.5 py-2 text-center"><p className="text-[9px] uppercase tracking-wider text-zinc-500">Since</p><p className="text-base font-bold text-white mt-0.5">{person.since || '—'}</p></div>
        </div>

        {/* Contact information (merged from the linked customer) */}
        <div className="px-6 pt-3">
          <div className="bg-gray-900/60 border border-gray-800/70 rounded-2xl px-4 py-2.5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Contact information</p>
            {copyRow(Phone, 'Primary phone', fmtPhone(person.phone), 'cp', 'text-emerald-300')}
            {copyRow(Smartphone, 'Secondary phone', fmtPhone(person.secondaryPhone), 'cs', 'text-emerald-300')}
            {copyRow(Mail, 'Primary email', person.email, 'ce', 'text-violet-300')}
            {copyRow(Mail, 'Alternate email', person.altEmail, 'ca', 'text-violet-300')}
          </div>
        </div>

        {/* Billing / job-site address */}
        <div className="px-6 pt-3">
          <div className="bg-gray-900/60 border border-gray-800/70 rounded-2xl px-4 py-2.5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Billing / job-site address</p>
            {addrFull ? (
              <button type="button" onClick={() => copy(addrFull, 'addr')} className="w-full text-left group flex items-start justify-between gap-3">
                <span className="text-sm text-zinc-200 leading-relaxed">{addr.street && <>{addr.street}<br /></>}{addrLine2 || person.city}</span>
                {copied === 'addr' ? <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> : <Copy className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 shrink-0 mt-0.5" />}
              </button>
            ) : <p className="text-sm text-zinc-600">No address on file.</p>}
          </div>
        </div>

        {/* Notes & tags — notes get their own quick edit, no need to open the full form */}
        <div className="px-6 pt-3">
          <div className="bg-gray-900/60 border border-gray-800/70 rounded-2xl px-4 py-2.5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Notes &amp; tags</p>
              {!notesEditing && (
                <button onClick={() => { setNotesDraft(person.notes || ''); setNotesEditing(true); }} title="Edit notes" className="p-1.5 rounded-lg hover:bg-gray-800 text-zinc-500 hover:text-zinc-300"><Pencil className="w-3.5 h-3.5" /></button>
              )}
            </div>
            {(person.tags || []).length > 0 && <div className="flex flex-wrap gap-1.5 mb-2">{person.tags.map(t => <span key={t} className="text-[11px] font-medium bg-indigo-500/15 text-indigo-300 px-2 py-0.5 rounded-full">{t}</span>)}</div>}
            {notesEditing ? (
              <div>
                <textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value)} rows={3} autoFocus
                  placeholder="Gate code, dog's name, how they like to be billed…"
                  className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60 resize-none" />
                <div className="flex gap-2 mt-2 mb-1">
                  <button onClick={() => setNotesEditing(false)} className="flex-1 py-1.5 bg-gray-800 hover:bg-gray-700 text-zinc-300 rounded-lg text-xs font-semibold">Cancel</button>
                  <button onClick={() => { onUpdate && onUpdate(person.id, { notes: notesDraft.trim() }); setNotesEditing(false); }} className="flex-1 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold">Save notes</button>
                </div>
              </div>
            ) : (
              person.notes ? <p className="text-sm text-zinc-400 leading-relaxed whitespace-pre-wrap">{person.notes}</p> : ((person.tags || []).length === 0 && <p className="text-sm text-zinc-600">No notes yet — tap the pencil.</p>)
            )}
          </div>
        </div>

        <div className="px-6 pt-4 pb-2">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{isRealPerson ? 'Jobs & paperwork' : 'Projects'}</p>
          {isRealPerson ? (
            !work ? <p className="text-zinc-600 text-sm">Loading…</p> : (
              <div className="space-y-1">
                {work.projects.map(p => (
                  <button key={p._id} onClick={() => navigate(`/projects/${p._id}`)} title="Open project"
                    className="w-full flex items-center gap-2 text-sm text-zinc-300 hover:bg-gray-800/60 rounded-lg px-2 py-1.5 text-left">
                    <FolderOpen className="w-3.5 h-3.5 text-sky-300 shrink-0" /><span className="truncate">PO {p.poNumber} — {p.title}</span><span className="text-[10px] text-zinc-600 ml-auto shrink-0">{p.status}</span>
                  </button>
                ))}
                {work.bids.map(b => (
                  <button key={b._id} onClick={() => onOpenBid && onOpenBid(b._id)} title="Open bid"
                    className="w-full flex items-center gap-2 text-sm text-zinc-300 hover:bg-gray-800/60 rounded-lg px-2 py-1.5 text-left">
                    <Briefcase className="w-3.5 h-3.5 text-indigo-300 shrink-0" /><span className="truncate">{b.title}</span><span className="text-[10px] text-zinc-600 ml-auto shrink-0">{b.stage}</span>
                  </button>
                ))}
                {work.quotes.map(q => (
                  <button key={q._id} onClick={() => navigate(`/billing/create-quote?id=${q._id}`)} title="Open quote"
                    className="w-full flex items-center gap-2 text-sm text-zinc-300 hover:bg-gray-800/60 rounded-lg px-2 py-1.5 text-left">
                    <FileText className="w-3.5 h-3.5 text-violet-300 shrink-0" /><span className="truncate">{q.number}</span><span className="text-[10px] text-zinc-600 ml-auto shrink-0">{q.status}</span>
                  </button>
                ))}
                {work.projects.length === 0 && work.bids.length === 0 && work.quotes.length === 0 && (
                  <p className="text-zinc-600 text-sm">Nothing yet — no projects, bids, or quotes.</p>
                )}
              </div>
            )
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(person.projectIds || []).map(pid => <span key={pid} className="text-[11px] font-medium bg-sky-500/10 text-sky-300 px-2 py-1 rounded-md inline-flex items-center gap-1"><FolderOpen className="w-3 h-3" /> {projectName(pid)}</span>)}
              {(person.projectIds || []).length === 0 && <span className="text-zinc-600 text-sm">Not tied to a project yet.</span>}
            </div>
          )}
        </div>

        {/* log conversation (with project) — form starts collapsed to keep the drawer calm */}
        <div className="px-6 py-4 border-t border-gray-800/60 mt-2">
          <button onClick={() => setLogOpen(o => !o)} className="w-full flex items-center justify-between group">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-500 group-hover:text-zinc-300 flex items-center gap-1.5"><Plus className={`w-3.5 h-3.5 transition-transform ${logOpen ? 'rotate-45' : ''}`} /> Log conversation</span>
            <ChevronDown className={`w-4 h-4 text-zinc-600 transition-transform ${logOpen ? 'rotate-180' : ''}`} />
          </button>
          {logOpen && (
            <div className="mt-3">
              <div className="flex gap-1.5 mb-3">{Object.entries(CHANNELS).map(([key, c]) => { const Icon = CHANNEL_ICON[key]; const on = channel === key; return <button key={key} onClick={() => setChannel(key)} className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold ${on ? `${c.bg} ${c.tone} ring-1 ring-white/10` : 'bg-gray-900 text-zinc-500 hover:text-zinc-300'}`}><Icon className="w-4 h-4" /> {c.label}</button>; })}</div>
              <div className="flex gap-2 mb-2"><button onClick={() => setDirection('outbound')} className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${direction === 'outbound' ? 'bg-indigo-600 text-white' : 'bg-gray-900 text-zinc-400'}`}><ArrowUpRight className="w-3.5 h-3.5" /> Outbound</button><button onClick={() => setDirection('inbound')} className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${direction === 'inbound' ? 'bg-emerald-600 text-white' : 'bg-gray-900 text-zinc-400'}`}><ArrowDownLeft className="w-3.5 h-3.5" /> Inbound</button></div>
              <select value={proj} onChange={e => setProj(e.target.value)} className="w-full mb-2 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60">
                <option value="">No project</option>
                {PROJECTS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input value={outcome} onChange={e => setOutcome(e.target.value)} placeholder="Outcome (e.g. Passed, Scheduled)" className="w-full mb-2 px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60" />
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="What was discussed?" className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60 resize-none" />
              <button onClick={submit} className="w-full mt-2 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-bold">Log {CHANNELS[channel].label.toLowerCase()}</button>
            </div>
          )}
        </div>

        {/* conversation timeline (project-tagged) */}
        <div className="px-6 py-4 border-t border-gray-800/60">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Conversations</p>
          <div className="relative pl-2"><div className="absolute left-[26px] top-2 bottom-2 w-px bg-gray-800" /><div className="space-y-4">
            {(person.activities || []).map((act) => { const DirIcon = act.direction === 'inbound' ? ArrowDownLeft : ArrowUpRight; const pn = act.projectId ? projectName(act.projectId) : null; return (
              <div key={act.id} onContextMenu={(e) => { e.preventDefault(); setActMenu({ act, x: e.clientX, y: e.clientY }); }} className="relative flex gap-3 fade-up"><div className="relative z-10 cursor-pointer" title="Click to edit this entry" onClick={() => startEditAct(act)}><ChannelBadge channel={act.channel} /></div>
                {editingActId === act.id ? (
                  <ActivityEditor outcome={editActOutcome} note={editActNote} setOutcome={setEditActOutcome} setNote={setEditActNote} onSave={saveEditAct} onCancel={() => setEditingActId(null)} />
                ) : (
                <div className="flex-1 min-w-0 bg-gray-900/70 border border-gray-800/70 rounded-xl px-3.5 py-2.5">
                  <div className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 text-xs font-semibold text-zinc-300"><DirIcon className={`w-3.5 h-3.5 ${act.direction === 'inbound' ? 'text-emerald-400' : 'text-indigo-400'}`} /><span className="capitalize">{act.channel}</span>{act.outcome && <span className="text-[10px] font-bold bg-zinc-700/50 text-zinc-300 px-1.5 py-0.5 rounded">{act.outcome}</span>}</span><span className="text-[11px] text-zinc-500 shrink-0">{timeAgo(act.at)}</span></div>
                  {act.note && <p className="text-sm text-zinc-400 mt-1 leading-snug">{act.note}</p>}
                  {pn && <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold bg-sky-500/10 text-sky-300 px-1.5 py-0.5 rounded"><FolderOpen className="w-3 h-3" /> {pn}</span>}
                </div>
                )}
              </div>
            ); })}
          </div></div>
        </div>
      </div>
      <ActivityContextMenu menu={actMenu} onClose={() => setActMenu(null)}
        onEdit={() => startEditAct(actMenu.act)}
        onDelete={() => { onDeleteActivity(person.id, actMenu.act.id); setActMenu(null); }} />

      {editing && form && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditing(false)} />
          <div className="relative w-[460px] max-w-full max-h-[85vh] overflow-y-auto bg-gray-950 border border-gray-800 rounded-2xl shadow-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-white">Edit contact</h3>
              <button onClick={() => setEditing(false)} className="p-1.5 rounded-lg hover:bg-gray-800 text-zinc-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              {field('Name', 'name')}
              <div className="grid grid-cols-2 gap-2">{field('Role / title', 'role')}{field('Company', 'companyName')}</div>
              <div className="grid grid-cols-2 gap-2">{field('Primary phone', 'phone')}{field('Secondary phone', 'secondaryPhone')}</div>
              <div className="grid grid-cols-2 gap-2">{field('Primary email', 'email')}{field('Alternate email', 'altEmail')}</div>
              <AddressFields form={form} setForm={setForm} />
              {field('Tags (comma-separated)', 'tags')}
              <div className="grid grid-cols-2 gap-2">{field('Lifetime billed ($)', 'lifetime')}{field('Client since', 'since')}</div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setEditing(false)} className="flex-1 py-2.5 bg-gray-800 hover:bg-gray-700 text-zinc-200 rounded-xl text-sm font-semibold">Cancel</button>
              <button onClick={saveEdit} className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-bold">Save changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ======================= ADD CONTACT =======================
function AddContactModal({ onClose, onAdd, customers = [] }) {
  const [f, setF] = useState({ company: '', fullName: '', contactName: '', title: '', type: 'Contractor', licenseClass: '', licenseNumber: '', relStage: 'Prospect', source: 'Cold outreach', phone: '', email: '', secondaryPhone: '', secondaryEmail: '', address: '' });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  // Link existing people to this company, or add brand-new ones inline —
  // so a relationship is born already connected (DMV + its ticket-closers).
  const [linked, setLinked] = useState([]);       // existing customers to attach
  const [linkQ, setLinkQ] = useState('');
  const [mcOpen, setMcOpen] = useState(false);    // main-contact autocomplete
  const mcMatches = mcOpen && f.contactName.trim().length >= 2
    ? customers.filter(c => `${c.name} ${c.company || ''}`.toLowerCase().includes(f.contactName.trim().toLowerCase())).slice(0, 6)
    : [];
  const pickMainContact = (c) => {
    // Prefill the rest of the form from their record — ONLY blanks, so
    // anything already typed wins.
    setF(prev => ({
      ...prev,
      contactName: c.name,
      phone: prev.phone || c.phone || '',
      email: prev.email || c.email || '',
      secondaryPhone: prev.secondaryPhone || c.secondaryPhone || '',
      secondaryEmail: prev.secondaryEmail || c.altEmail || '',
      company: prev.company || c.company || '',
      address: prev.address || (c.address ? [c.address.street, c.address.city, c.address.state, c.address.zip].filter(Boolean).join(', ') : ''),
    }));
    setMcOpen(false);
    // picking an existing person also LINKS them (company + address flow)
    setLinked(p => p.some(x => x.id === c.id) ? p : [...p, c]);
  };
  const [newPeople, setNewPeople] = useState([]); // [{name, phone, email}]
  const linkMatches = linkQ.trim().length >= 2
    ? customers.filter(c => !linked.some(l => l.id === c.id) && `${c.name} ${c.company || ''}`.toLowerCase().includes(linkQ.trim().toLowerCase())).slice(0, 6)
    : [];
  const save = () => {
    if (!f.company.trim()) return;
    onAdd({
      id: `c${Date.now()}`, ...f, starred: false, website: '', units: null, tags: [], nextFollowUp: null, people: [], activities: [],
      _linkedIds: linked.map(l => l.id),
      _newPeople: newPeople.filter(p => p.name.trim()),
    });
  };
  // Google Places autocomplete for the address (typed text is kept as-is, so
  // manual entry is always available — suggestions are just a shortcut)
  const [customSource, setCustomSource] = useState(false); // "Other…" → free-text connection type
  const [addrSugs, setAddrSugs] = useState([]);
  const [addrErr, setAddrErr] = useState(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const addrTimer = useRef(null);
  const onAddr = (val) => {
    set('address', val);
    clearTimeout(addrTimer.current);
    if (val.trim().length < 3) { setAddrSugs([]); setAddrOpen(false); return; }
    addrTimer.current = setTimeout(async () => {
      const { predictions, error } = await getAddressSuggestions(val);
      setAddrErr(error); setAddrSugs(predictions); setAddrOpen(true);
    }, 350);
  };
  const input = "w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60";
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h2 className="text-xl font-bold">New Contact</h2><button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-zinc-400"><X className="w-5 h-5" /></button></div>
        <div className="space-y-3">
          <input className={input} placeholder="Company *" value={f.company} onChange={e => set('company', e.target.value)} autoFocus />
          <div className="grid grid-cols-2 gap-3"><div className="relative">
              <input className={input} placeholder="Main contact (optional)" value={f.contactName}
                onChange={e => { set('contactName', e.target.value); setMcOpen(true); }}
                onFocus={() => setMcOpen(true)}
                onBlur={() => setTimeout(() => setMcOpen(false), 180)} />
              {mcMatches.length > 0 && (
                <div className="absolute z-40 left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
                  {mcMatches.map((c) => (
                    <button key={c.id} onMouseDown={(e) => { e.preventDefault(); pickMainContact(c); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-800 text-left">
                      <span className="text-sm text-zinc-200 truncate">{c.name}</span>
                      {c.company && <span className="text-[11px] text-zinc-500 ml-auto shrink-0">{c.company}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div><input className={input} placeholder="Title" value={f.title} onChange={e => set('title', e.target.value)} /></div>

          {/* ---- People at this company: link existing, or add new inline ---- */}
          <div className="p-3 bg-gray-950/50 border border-gray-800/70 rounded-xl space-y-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">People at this company <span className="normal-case font-normal text-zinc-600">(optional — link or create)</span></p>
            {linked.map((l) => (
              <div key={l.id} className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2">
                <span className="text-sm text-zinc-200 flex-1 min-w-0 truncate">{l.name}{l.company ? <span className="text-zinc-600"> — {l.company}</span> : null}</span>
                <button onClick={() => setLinked(p => p.filter(x => x.id !== l.id))} className="text-zinc-600 hover:text-red-400">×</button>
              </div>
            ))}
            {newPeople.map((p, i) => (
              <div key={`np${i}`} className="grid grid-cols-[1fr_110px_1fr_24px] max-md:grid-cols-2 gap-2">
                <input className={input} placeholder="Name *" value={p.name} onChange={e => setNewPeople(ps => ps.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                <input className={input} placeholder="Phone" value={p.phone} onChange={e => setNewPeople(ps => ps.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))} />
                <input className={input} placeholder="Email" value={p.email} onChange={e => setNewPeople(ps => ps.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} />
                <button onClick={() => setNewPeople(ps => ps.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400 text-lg">×</button>
              </div>
            ))}
            <div className="relative">
              <input className={input} placeholder="Search existing people to link…" value={linkQ} onChange={e => setLinkQ(e.target.value)} />
              {linkMatches.length > 0 && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
                  {linkMatches.map((c) => (
                    <button key={c.id} onClick={() => { setLinked(p => [...p, c]); setLinkQ(''); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-800 text-left">
                      <span className="text-sm text-zinc-200 truncate">{c.name}</span>
                      {c.company && <span className="text-[11px] text-zinc-500 ml-auto shrink-0">{c.company}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setNewPeople(ps => [...ps, { name: '', phone: '', email: '' }])} className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold">+ New person at this company</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NiceSelect value={f.type} onChange={v => set('type', v)} options={REL_TYPES} placeholder="Type" />
            <NiceSelect value={f.relStage} onChange={v => set('relStage', v)} options={REL_STAGES} placeholder="Stage" />
          </div>
          {f.type === 'Government / Public Agency' && (
            <input className={input} placeholder="Full agency name (e.g. Department of Motor Vehicles)" value={f.fullName} onChange={e => set('fullName', e.target.value)} />
          )}
          {f.type === 'Contractor' && (
            <div className="grid grid-cols-2 gap-3">
              <NiceSelect value={f.licenseClass} onChange={v => set('licenseClass', v)}
                options={[{ value: '', label: 'License classification (optional)' }, ...CSLB_CLASSES]}
                placeholder="License classification (optional)" />
              <input
                className={input}
                inputMode="numeric"
                placeholder="CSLB License # (optional)"
                value={f.licenseNumber}
                onChange={e => set('licenseNumber', e.target.value.replace(/[^\d]/g, '').slice(0, 8))}
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3"><input className={input} placeholder="Phone" value={f.phone} onChange={e => set('phone', fmtPhoneInput(e.target.value))} /><input className={input} type="email" placeholder="Email" value={f.email} onChange={e => set('email', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3"><input className={input} placeholder="Secondary phone (optional)" value={f.secondaryPhone} onChange={e => set('secondaryPhone', fmtPhoneInput(e.target.value))} /><input className={input} type="email" placeholder="Secondary email (optional)" value={f.secondaryEmail} onChange={e => set('secondaryEmail', e.target.value)} /></div>
          <div className="relative">
            <input className={input} placeholder="Address — start typing to search…" value={f.address}
              onChange={e => onAddr(e.target.value)}
              onFocus={() => { if (addrSugs.length || addrErr) setAddrOpen(true); }}
              onBlur={() => setTimeout(() => setAddrOpen(false), 250)} />
            {addrOpen && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-xl max-h-48 overflow-auto z-[70] shadow-xl">
                {addrErr ? <div className="px-3 py-2.5 text-red-400 text-xs">{addrErr}</div>
                  : addrSugs.length === 0 ? <div className="px-3 py-2.5 text-zinc-500 text-xs">No matching addresses — your typed text is kept</div>
                  : addrSugs.map((p, i) => (
                    <div key={i} onClick={() => { set('address', p.description); setAddrOpen(false); }}
                      className="px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-zinc-200 border-b border-gray-700/50 last:border-0">{p.description}</div>
                  ))}
              </div>
            )}
          </div>
          <NiceSelect
            value={customSource ? '__other' : f.source}
            onChange={(v) => {
              if (v === '__other') { setCustomSource(true); set('source', ''); }
              else { setCustomSource(false); set('source', v); }
            }}
            options={[...SOURCES.map(s => ({ value: s, label: `How we connected: ${s}` })), { value: '__other', label: 'How we connected: Other…' }]}
            placeholder="How we connected"
          />
          {customSource && (
            <input className={input} placeholder="How did you connect? (e.g. Golf league, Supply house counter)" value={f.source} onChange={e => set('source', e.target.value)} autoFocus />
          )}
        </div>
        <div className="flex justify-end gap-3 mt-6"><button onClick={onClose} className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm font-semibold">Cancel</button><button onClick={save} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-bold">Add Contact</button></div>
      </div>
    </div>
  );
}

// ======================= ADD CONTACT (one smart, type-aware form) =======================
// EVERY contact is a person. You pick what they are; the form adapts. There are only
// two shapes: a DIRECT CLIENT (you work for them — homeowner/owner, no company) and a
// PERSON AT A COMPANY (their name + their role/title + the company they work at).
// The "kind" you pick just pre-fills their role and decides which shape to show.
const ROLE_GROUPS = [
  { group: 'Direct client — you work for them', roles: ['Homeowner', 'Property Owner', 'Tenant'] },
  { group: 'General contractor', roles: ['GC — Project Manager', 'GC — Superintendent', 'GC — Estimator', 'GC — Project Engineer', 'GC — Owner / Principal', 'GC — Project Coordinator'] },
  { group: 'Property management', roles: ['Property Manager', 'Maintenance Superintendent', 'Maintenance Coordinator', 'Facilities / Building Owner', 'HOA Board Member'] },
  { group: 'Design & engineering', roles: ['Architect', 'Interior Designer', 'Electrical Engineer', 'Mechanical Engineer'] },
  // Fellow trades + anyone who refers work back and forth — the business-card
  // people. They're not customers; they're the referral network.
  { group: 'Trades & referral partners', roles: ['Electrician', 'Plumber', 'HVAC', 'Roofer', 'Painter', 'Drywall', 'Landscaper', 'Concrete', 'Framer', 'Handyman', 'Solar', 'Low Voltage / AV', 'Other Trade'] },
  { group: 'Other industry', roles: ['Inspector', 'Realtor', 'Developer', 'Vendor / Supplier Rep', 'Other / Custom'] },
];
const DIRECT_CLIENT_ROLES = ['Homeowner', 'Property Owner', 'Tenant'];

function AddPersonModal({ contacts, onClose, onAdd, presetCompany = '' }) {
  const [f, setF] = useState({
    // arriving from a company drawer → company pre-filled and a company-ish default role
    role: presetCompany ? 'GC — Project Manager' : 'Homeowner',
    title: '', customRole: '', company: presetCompany,
    first: '', middle: '', last: '', nickname: '', status: 'Active', phone: '', phone2: '', email: '', email2: '', address: '',
  });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  // Google Places autocomplete for the address — same shortcut the
  // relationship form has (typed text is always kept; suggestions optional).
  const [addrSugs, setAddrSugs] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const addrTimer = useRef(null);
  const onAddr = (val) => {
    set('address', val);
    clearTimeout(addrTimer.current);
    if (val.trim().length < 3) { setAddrSugs([]); setAddrOpen(false); return; }
    addrTimer.current = setTimeout(async () => {
      const { predictions } = await getAddressSuggestions(val);
      setAddrSugs(predictions || []); setAddrOpen(true);
    }, 350);
  };

  const isDirectClient = DIRECT_CLIENT_ROLES.includes(f.role);
  const isCustom = f.role === 'Other / Custom';
  // The role as it should read in the directory (strip the "GC — " grouping prefix).
  const baseRole = isCustom ? (f.customRole.trim() || 'Contact') : f.role.replace(/^GC — /, '');
  const personName = [f.first, f.middle, f.last].map(s => s.trim()).filter(Boolean).join(' ');
  const canSave = !!personName; // a contact is a person — always need their name

  const hint = isDirectClient
    ? 'A client you work for directly. Their name, how to reach them, and the job address — no company needed.'
    : 'A person at a company you deal with. Put THEIR name, their role/title, and the company they work for.';

  const save = () => {
    if (!canSave) return;
    const parts = String(f.address || '').split(',');
    const city = parts.length >= 2 ? parts[parts.length - 2].trim() : '';
    const role = (f.title.trim() || baseRole);
    onAdd({
      id: `cu${Date.now()}`, name: personName, type: role,
      // The picked KIND (Plumber, Architect, GC — …) kept separately: a typed
      // job title used to REPLACE it entirely, which made searching "plumber"
      // miss a plumber whose title was "Owner".
      role: baseRole,
      title: f.title.trim(),
      status: isDirectClient ? f.status : 'Contact',
      company: isDirectClient ? '' : f.company.trim(),
      nickname: f.nickname.trim(),
      phone: f.phone, phone2: f.phone2, email: f.email, email2: f.email2,
      address: f.address.trim(), city,
    });
  };

  const input = "w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60";
  const lbl = "block text-zinc-400 text-sm mb-1";
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-2xl p-8 max-md:p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h2 className="text-2xl font-bold">Add Contact</h2><button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-zinc-400"><X className="w-5 h-5" /></button></div>

        {/* Step 1 — what kind of person? Pre-fills their role + chooses the field shape. */}
        <div className="mb-5">
          <label className={lbl}>What kind of contact?</label>
          <select className={input + ' text-base font-medium'} value={f.role} onChange={e => set('role', e.target.value)} autoFocus>
            {ROLE_GROUPS.map(g => (
              <optgroup key={g.group} label={g.group}>
                {g.roles.map(r => <option key={r} value={r}>{r.replace(/^GC — /, '')}</option>)}
              </optgroup>
            ))}
          </select>
          {isCustom && (
            <input className={input + ' mt-2'} placeholder="Type their role (e.g. Solar Consultant)" value={f.customRole} onChange={e => set('customRole', e.target.value)} />
          )}
          <p className="text-[12px] text-indigo-300/80 mt-2 leading-relaxed">{hint}</p>
        </div>

        {/* The person — always. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div><label className={lbl}>First Name *</label><input className={input} value={f.first} onChange={e => set('first', e.target.value)} /></div>
          <div><label className={lbl}>Middle Name</label><input className={input} value={f.middle} onChange={e => set('middle', e.target.value)} /></div>
          <div><label className={lbl}>Nickname <span className="text-zinc-600">(optional)</span></label><input className={input} placeholder='e.g. "Big Mike"' value={f.nickname} onChange={e => set('nickname', e.target.value)} /></div>
          <div><label className={lbl}>Last Name</label><input className={input} value={f.last} onChange={e => set('last', e.target.value)} /></div>
        </div>

        {/* Direct client → status. Person-at-company → company + their title there. */}
        {isDirectClient ? (
          <div className="mt-5 max-w-xs">
            <label className={lbl}>Status</label>
            <select className={input} value={f.status} onChange={e => set('status', e.target.value)}>
              {['Active', 'Lead', 'Inactive', 'Lost'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            <div>
              <label className={lbl}>Company / firm <span className="text-zinc-600">— where they work</span></label>
              <input list="addperson-companies" className={input} placeholder="e.g. Skyline Builders" value={f.company} onChange={e => set('company', e.target.value)} />
              <datalist id="addperson-companies">{(contacts || []).map(c => <option key={c.id} value={c.company} />)}</datalist>
            </div>
            <div>
              <label className={lbl}>Role / title there</label>
              <input className={input} placeholder={baseRole} value={f.title} onChange={e => set('title', e.target.value)} />
              <p className="text-[11px] text-zinc-600 mt-1">Defaults to “{baseRole}.” Edit for an exact title (e.g. Senior PM).</p>
            </div>
          </div>
        )}

        {/* Reach them — same for everyone. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          <div><label className={lbl}>Primary Phone</label><input className={input} placeholder="xxx-xxx-xxxx" value={f.phone} onChange={e => set('phone', fmtPhoneInput(e.target.value))} /></div>
          <div><label className={lbl}>Secondary Phone</label><input className={input} placeholder="xxx-xxx-xxxx" value={f.phone2} onChange={e => set('phone2', fmtPhoneInput(e.target.value))} /></div>
          <div><label className={lbl}>Primary Email</label><input className={input} type="email" value={f.email} onChange={e => set('email', e.target.value)} /></div>
          <div><label className={lbl}>Alternate Email</label><input className={input} type="email" value={f.email2} onChange={e => set('email2', e.target.value)} /></div>
        </div>

        <div className="mt-5">
          <label className={lbl}>{isDirectClient ? 'Job / property address' : 'Address'} (optional)</label>
          <div className="relative">
            <input className={input} placeholder="Start typing…" value={f.address}
              onChange={e => onAddr(e.target.value)}
              onBlur={() => setTimeout(() => setAddrOpen(false), 180)} />
            {addrOpen && addrSugs.length > 0 && (
              <div className="absolute z-40 left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden max-h-56 overflow-y-auto">
                {addrSugs.map((s) => (
                  <button key={s.place_id} onMouseDown={(e) => { e.preventDefault(); set('address', s.description); setAddrOpen(false); }}
                    className="w-full px-3 py-2 hover:bg-gray-800 text-left text-sm text-zinc-200">
                    {s.description}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="text-[11px] text-zinc-600 mt-1">Live, this becomes Google Places autocomplete (same as Customers).</p>
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <button onClick={onClose} className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium">Cancel</button>
          <button onClick={save} disabled={!canSave} className={`px-6 py-3 rounded-xl font-medium shadow-md text-white ${canSave ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-gray-700 cursor-not-allowed opacity-60'}`}>Save Contact</button>
        </div>
      </div>
    </div>
  );
}

// ======================= ADD BID =======================
// "$1,234.56" as you type — raw digits/decimal live in state, dollars on screen.
const fmtMoneyTyping = (v) => {
  const raw = String(v || '').replace(/[^\d.]/g, '');
  if (!raw) return '';
  const [int, dec] = raw.split('.');
  const intFmt = int ? Number(int).toLocaleString('en-US') : '0';
  return '$' + intFmt + (dec !== undefined ? '.' + dec.slice(0, 2) : '');
};

function AddBidModal({ customers, contacts, people, sources, setSources, onClose, onAdd, onUploadPhotos }) {
  const [f, setF] = useState({ title: '', customerId: '', value: '', stage: 'Lead', source: sources[0] || '', referredBy: '', street: '', city: '', zip: '', notes: '', due: '' });
  const [err, setErr] = useState('');
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  // Who: existing customer OR a typed contact (bids are usually for people
  // who aren't customers yet — nothing enters Customers until the bid is won).
  const [custMode, setCustMode] = useState('new'); // 'new' | 'existing'
  const [contact, setContact] = useState({ name: '', phone: '', email: '' });

  // Job address: Places search bar + manual-entry fallback.
  const [addrQuery, setAddrQuery] = useState('');
  const [addrPicked, setAddrPicked] = useState('');
  const [addrSugs, setAddrSugs] = useState([]);
  const [manualAddr, setManualAddr] = useState(false);
  useEffect(() => {
    const q = addrQuery.trim();
    if (manualAddr || q.length < 3 || q === addrPicked) { setAddrSugs([]); return; }
    const t = setTimeout(() => {
      getAddressSuggestions(q)
        .then(r => setAddrSugs((r.predictions || []).slice(0, 5)))
        .catch(() => setAddrSugs([]));
    }, 300);
    return () => clearTimeout(t);
  }, [addrQuery, addrPicked, manualAddr]);
  const pickAddr = async (s) => {
    setAddrQuery(s.description); setAddrPicked(s.description); setAddrSugs([]);
    // split the formatted address into the model's street/city/zip
    try {
      const parts = s.description.split(',').map(x => x.trim());
      const street = parts[0] || '';
      const city = parts[1] || '';
      const zip = ((parts[2] || '').match(/\d{5}/) || [''])[0];
      setF(prev => ({ ...prev, street, city, zip }));
    } catch (_) { setF(prev => ({ ...prev, street: s.description })); }
  };

  // Jobsite photos — staged locally, uploaded right after the bid is created.
  const [photos, setPhotos] = useState([]); // File[]
  const [photoDrag, setPhotoDrag] = useState(false);
  const [saving, setSaving] = useState(false);
  const photoInputRef = useRef(null);
  // Duplicate names are rejected — sample.jpg vs sample.heic is fine, but
  // .jpg and .jpeg count as the SAME name (sample.jpg blocks sample.jpeg).
  const photoKey = (n) => String(n || '').toLowerCase().replace(/\.jpeg$/, '.jpg');
  const addPhotos = (list) => {
    const incoming = Array.from(list || []).filter(x => x.size);
    const have = new Set(photos.map(p => photoKey(p.name)));
    const accepted = [];
    const skipped = [];
    incoming.forEach(x => {
      const k = photoKey(x.name);
      if (have.has(k)) { skipped.push(x.name); return; }
      have.add(k);
      accepted.push(x);
    });
    if (accepted.length) setPhotos(prev => [...prev, ...accepted]);
    if (skipped.length) window.alert(`Skipped — same name already added: ${skipped.join(', ')}`);
  };
  const isReferral = f.source === 'Referral' || f.source === 'Word of mouth';
  const [managing, setManaging] = useState(false);
  const [newSource, setNewSource] = useState('');
  // Add = add + select + done. Empty field = just close, select nothing new.
  const addSource = () => {
    const v = newSource.trim();
    if (v) {
      if (!sources.includes(v)) setSources([...sources, v]);
      set('source', v);
    }
    setNewSource('');
    setManaging(false);
  };
  const removeSource = (s) => { const next = sources.filter(x => x !== s); setSources(next); if (f.source === s) set('source', next[0] || ''); };
  const save = async () => {
    if (!f.title.trim()) { setErr('Job title is required'); return; }
    if (custMode === 'existing' && !f.customerId) { setErr('Pick a customer, or switch to “New contact”'); return; }
    if (custMode === 'new' && !contact.name.trim()) { setErr('Enter a contact name (who do you call about this job?)'); return; }
    setSaving(true);
    try {
      const created = await onAdd({
        title: f.title.trim(),
        customer: custMode === 'existing' ? f.customerId : null,
        contactName: custMode === 'new' ? contact.name.trim() : '',
        contactPhone: custMode === 'new' ? contact.phone.trim() : '',
        contactEmail: custMode === 'new' ? contact.email.trim() : '',
        value: Number(f.value) || 0,
        stage: f.stage,
        source: f.source,
        notes: [f.notes.trim(), isReferral && f.referredBy.trim() ? `Referred by ${f.referredBy.trim()}` : ''].filter(Boolean).join(' · '),
        due: f.due ? new Date(f.due + 'T09:00:00').toISOString() : null,
        jobSiteAddress: { street: f.street, city: f.city, state: 'CA', zip: f.zip, country: 'USA' },
      });
      if (!created || !created.id) throw new Error('The bid was not created — nothing was uploaded.');
      // Close right away — photos upload in the background and the bid's card
      // wears a progress border until they're all in Dropbox.
      onClose();
      onUploadPhotos(created.id, photos);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };
  // Anyone you know can be a referrer: relationship companies + their
  // contacts, people, AND your real customers.
  const referrerNames = Array.from(new Set([
    ...contacts.map(c => c.contactName), ...contacts.map(c => c.company),
    ...(people || []).map(p => p.name),
    ...(customers || []).map(c => c.name), ...(customers || []).map(c => c.company),
  ].filter(Boolean)));
  const input = "w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60";
  const lbl = "block text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-1";
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1"><h2 className="text-xl font-bold">New Bid</h2><button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-zinc-400"><X className="w-5 h-5" /></button></div>
        <p className="text-xs text-zinc-500 mb-5">Track an opportunity in your pipeline — the actual ITB still lives on PlanHub/etc. This is so you know what’s in flight and where it came from.</p>
        <div className="space-y-3">
          <div><label className={lbl}>Job title *</label><input className={input} placeholder="e.g. Johnson — fan install" value={f.title} onChange={e => set('title', e.target.value)} autoFocus /></div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={lbl + ' mb-0'}>{custMode === 'new' ? 'Contact *' : 'Customer *'}</label>
              <button type="button" onClick={() => setCustMode(m => m === 'new' ? 'existing' : 'new')} className="text-[11px] font-semibold text-indigo-400 hover:text-indigo-300">
                {custMode === 'new' ? 'Pick an existing customer instead' : 'New contact instead'}
              </button>
            </div>
            {custMode === 'existing' ? (
              <NiceSelect value={f.customerId} onChange={v => set('customerId', v)}
                options={[...customers].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => ({ value: c.id, label: `${c.name} (${c.type})` }))}
                placeholder="— pick a customer —" />
            ) : (
              <div className="space-y-2">
                <input className={input} placeholder="Name — who do you call? *" value={contact.name} onChange={e => setContact(c => ({ ...c, name: e.target.value }))} />
                <div className="grid grid-cols-2 max-md:grid-cols-1 gap-2">
                  <input className={input} type="tel" placeholder="Phone (optional)" value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: fmtPhoneInput(e.target.value) }))} />
                  <input className={input} type="email" placeholder="Email (optional)" value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))} />
                </div>
                <p className="text-[11px] text-zinc-600">Nothing enters Customers until this bid is won — winning creates the customer automatically.</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Value — your bid</label><input className={input} type="text" inputMode="decimal" placeholder="$0.00" value={fmtMoneyTyping(f.value)} onChange={e => set('value', e.target.value.replace(/[^\d.]/g, ''))} /></div>
            <div><label className={lbl}>Stage</label><NiceSelect value={f.stage} onChange={v => set('stage', v)} options={BID_STAGES.filter(s => s !== 'Won')} placeholder="Stage" /></div>
          </div>
          <div>
            <label className={lbl}>Jobsite address (optional)</label>
            {!manualAddr ? (
              <div>
                <input
                  className={input}
                  placeholder="Type to search address…"
                  value={addrQuery}
                  onChange={e => setAddrQuery(e.target.value)}
                />
                {addrSugs.length > 0 && (
                  <div className="mt-1 border border-gray-700 rounded-xl overflow-hidden bg-gray-800">
                    {addrSugs.map(s => (
                      <button
                        type="button"
                        key={s.placeId || s.description}
                        onClick={() => pickAddr(s)}
                        className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0"
                      >
                        📍 {s.description}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-4 max-md:grid-cols-2 gap-3">
                <div className="col-span-2"><input className={input} placeholder="Street — e.g. 88 Almond Orchard Ln" value={f.street} onChange={e => set('street', e.target.value)} /></div>
                <div><input className={input} placeholder="City" value={f.city} onChange={e => set('city', e.target.value)} /></div>
                <div><input className={input} placeholder="ZIP" value={f.zip} onChange={e => set('zip', e.target.value)} /></div>
              </div>
            )}
            <button type="button" onClick={() => setManualAddr(m => !m)} className="mt-1.5 text-[11px] font-semibold text-indigo-400 hover:text-indigo-300 hover:underline underline-offset-2 block">
              {manualAddr ? 'Search with Google instead' : 'Enter custom address'}
            </button>
          </div>
          <div>
            <label className={lbl}>Jobsite files (optional)</label>
            <div
              onDragOver={e => { e.preventDefault(); setPhotoDrag(true); }}
              onDragLeave={() => setPhotoDrag(false)}
              onDrop={e => { e.preventDefault(); setPhotoDrag(false); addPhotos(e.dataTransfer.files); }}
              onClick={() => photoInputRef.current && photoInputRef.current.click()}
              className={`rounded-xl border-2 border-dashed px-4 py-5 text-center cursor-pointer transition-colors ${photoDrag ? 'border-indigo-500 bg-indigo-600/10' : 'border-gray-700 bg-gray-800/40 hover:border-gray-600'}`}
            >
              <Camera className="w-5 h-5 mx-auto text-zinc-500 mb-1" />
              <p className="text-xs text-zinc-400 font-medium">
                <span className="md:hidden">Tap to add photos, videos, or documents</span>
                <span className="max-md:hidden">Drag &amp; drop photos, videos, or documents — each lands in its right folder</span>
              </p>
              <p className="text-[10px] text-zinc-600 mt-0.5">They stage in this year’s /Bids folder and move into the project when you win.</p>
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*,.heic,.heif,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
              multiple
              className="hidden"
              onChange={e => { addPhotos(e.target.files); e.target.value = ''; }}
            />
            {photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {photos.map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-gray-800 text-zinc-300 px-2 py-1 rounded max-w-[180px]">
                    <ImageIcon className="w-3 h-3 text-zinc-500 shrink-0" /><span className="truncate">{p.name}</span>
                    <button type="button" onClick={() => setPhotos(prev => prev.filter((_, x) => x !== i))} className="text-zinc-500 hover:text-red-400 shrink-0"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div><label className={lbl}>Follow-up / due date (optional)</label><input className={input} type="date" value={f.due} onChange={e => set('due', e.target.value)} /></div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={lbl + ' mb-0'}>Source — where did this come from?</label>
              <button type="button" onClick={() => setManaging(m => !m)} className="text-[11px] font-semibold text-indigo-400 hover:text-indigo-300">{managing ? 'Done' : 'Manage sources'}</button>
            </div>
            <NiceSelect value={f.source} onChange={v => set('source', v)} options={sources} placeholder="Source" />
            {managing && (
              <div className="mt-2 p-3 bg-gray-800/40 border border-gray-700 rounded-xl space-y-2.5">
                <div className="flex flex-wrap gap-1.5">
                  {sources.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-gray-800 text-zinc-300 px-2 py-1 rounded">
                      {s}<button type="button" onClick={() => removeSource(s)} className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {sources.length === 0 && <span className="text-zinc-600 text-xs">No sources — add one below.</span>}
                </div>
                <div className="flex gap-2">
                  <input
                    value={newSource}
                    onChange={e => setNewSource(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSource(); } }}
                    placeholder="Add a source (e.g. Facebook Ads)"
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60"
                  />
                  <button type="button" onClick={addSource} className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-bold">Add</button>
                </div>
              </div>
            )}
          </div>
          {isReferral && (
            <div>
              <label className={lbl}>Referred by (optional)</label>
              <input className={input} placeholder="Who sent them your way?" value={f.referredBy} onChange={e => set('referredBy', e.target.value)} />
              {(() => {
                const q = f.referredBy.trim().toLowerCase();
                if (!q) return null;
                const hits = referrerNames.filter(n => n.toLowerCase().includes(q) && n.toLowerCase() !== q).slice(0, 5);
                if (!hits.length) return null;
                return (
                  <div className="mt-1 border border-gray-700 rounded-xl overflow-hidden bg-gray-800">
                    {hits.map(n => (
                      <button
                        type="button"
                        key={n}
                        onClick={() => set('referredBy', n)}
                        className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0"
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                );
              })()}
              <p className="text-[11px] text-zinc-600 mt-1">Pick a known contact or type a name — tracks your best referral sources.</p>
            </div>
          )}
          <div><label className={lbl}>Notes (optional)</label><textarea rows={2} className={input + ' resize-none'} placeholder="Anything worth remembering about this job" value={f.notes} onChange={e => set('notes', e.target.value)} /></div>
        </div>
        {err && <p className="text-red-400 text-sm mt-3">{err}</p>}
        <div className="flex justify-end gap-3 mt-6"><button onClick={onClose} className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm font-semibold">Cancel</button><button onClick={save} disabled={saving} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white rounded-xl text-sm font-bold">{saving ? 'Saving…' : 'Add Bid'}</button></div>
      </div>
    </div>
  );
}
